import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLanguage } from "@/context/LanguageContext";
import axios from "axios";
import Draggable from "react-draggable";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ArrowLeft, Plus, Upload, Save, Eye, Trash2, Type, DollarSign, Maximize2, Settings, Grid3X3, Ruler, ZoomIn, ZoomOut, Hand, MousePointer2, Pencil, Check, X, Scissors, Copy, ClipboardPaste, Undo2, Redo2, Image, Layers, ArrowUp, ArrowDown, ChevronsUp, ChevronsDown, Download, Video, Film, Link, Volume2, VolumeX, Play, Pause, RefreshCw, Sparkles, Clock, Cloud, Rss, MessageSquare, Square, Circle, Triangle, Star, Hexagon, Minus, ArrowRight, RotateCw, Combine } from "lucide-react";
import html2canvas from "html2canvas";
import LanguageSelector from "@/components/LanguageSelector";
import { compressImage } from "@/utils/imageCompression";
import MediaLibrary from "@/components/MediaLibrary";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Inline translation helper for toast messages
const getToastMsg = (language, key) => {
  const messages = {
    errorLoadingMenu: { en: "Error loading menu", es: "Error al cargar el menú", pt: "Erro ao carregar menu", ht: "Erè nan chajman meni" },
    drawModeCanceled: { en: "Draw mode canceled", es: "Modo de dibujo cancelado", pt: "Modo de desenho cancelado", ht: "Mòd desen anile" },
    imageLoaded: { en: "Image loaded", es: "Imagen cargada", pt: "Imagem carregada", ht: "Imaj chaje" },
    errorLoadingImage: { en: "Error loading image", es: "Error al cargar la imagen", pt: "Erro ao carregar imagem", ht: "Erè nan chajman imaj" },
    errorSavingMenu: { en: "Error saving menu", es: "Error al guardar el menú", pt: "Erro ao salvar menu", ht: "Erè nan anrejistre meni" },
    errorExportingImage: { en: "Error exporting image", es: "Error al exportar imagen", pt: "Erro ao exportar imagem", ht: "Erè nan ekspòte imaj" },
    enterText: { en: "Enter text", es: "Ingresa el texto", pt: "Digite o texto", ht: "Antre tèks la" },
    unsupportedFormat: { en: "Unsupported format. Use MP4, WebM, MOV or GIF", es: "Formato no soportado. Usa MP4, WebM, MOV o GIF", pt: "Formato não suportado. Use MP4, WebM, MOV ou GIF", ht: "Fòma pa sipòte. Itilize MP4, WebM, MOV oswa GIF" },
    fileTooLarge: { en: "File too large. Max 10MB", es: "El archivo es muy grande. Máximo 10MB", pt: "Arquivo muito grande. Máximo 10MB", ht: "Fichye twò gwo. Maksimòm 10MB" },
    videoAdded: { en: "Video added", es: "Video agregado", pt: "Vídeo adicionado", ht: "Videyo ajoute" },
    gifAdded: { en: "GIF added", es: "GIF agregado", pt: "GIF adicionado", ht: "GIF ajoute" },
    errorUploadingVideo: { en: "Error uploading video", es: "Error al subir el video", pt: "Erro ao enviar vídeo", ht: "Erè nan voye videyo" },
    enterValidUrl: { en: "Enter a valid URL", es: "Ingresa una URL válida", pt: "Digite uma URL válida", ht: "Antre yon URL valid" },
    invalidYoutubeUrl: { en: "Invalid YouTube URL", es: "URL de YouTube no válida", pt: "URL do YouTube inválida", ht: "URL YouTube pa valid" },
    invalidVimeoUrl: { en: "Invalid Vimeo URL", es: "URL de Vimeo no válida", pt: "URL do Vimeo inválida", ht: "URL Vimeo pa valid" },
    cityApiKeyRequired: { en: "City and API Key required", es: "Ciudad y API Key son requeridos", pt: "Cidade e API Key obrigatórios", ht: "Vil ak API Key obligatwa" },
    tickerTextRequired: { en: "Ticker text is required", es: "El texto del ticker es requerido", pt: "O texto do ticker é obrigatório", ht: "Tèks ticker obligatwa" },
    selectPresetOrRss: { en: "Select a preset or enter RSS URL", es: "Selecciona un preset o ingresa una URL RSS", pt: "Selecione um preset ou digite URL RSS", ht: "Chwazi yon preset oswa antre URL RSS" },
    clickDragToDrawShape: { en: "Click and drag on canvas to draw shape", es: "Haz clic y arrastra en el canvas para dibujar la forma", pt: "Clique e arraste no canvas para desenhar a forma", ht: "Klike epi trennen sou canvas pou desine fòm nan" },
    shapeTooSmall: { en: "Shape too small. Drag more to create larger shape", es: "Forma muy pequeña. Arrastra más para crear una forma mayor", pt: "Forma muito pequena. Arraste mais para criar uma forma maior", ht: "Fòm twò piti. Trennen plis pou kreye yon fòm pi gwo" },
    enterVideoUrl: { en: "Enter video URL", es: "Ingresa una URL de video", pt: "Digite uma URL de vídeo", ht: "Antre URL videyo" },
    videoAddedFromUrl: { en: "Video added from URL", es: "Video agregado desde URL", pt: "Vídeo adicionado da URL", ht: "Videyo ajoute depi URL" },
    selectMinTwoElements: { en: "Select at least 2 elements to merge", es: "Selecciona al menos 2 elementos para fusionar", pt: "Selecione pelo menos 2 elementos para mesclar", ht: "Chwazi omwen 2 eleman pou fimen" },
    selectGroupToSeparate: { en: "Select a group to separate", es: "Selecciona un grupo para separar", pt: "Selecione um grupo para separar", ht: "Chwazi yon gwoup pou separe" },
    groupSeparated: { en: "Group separated", es: "Grupo separado", pt: "Grupo separado", ht: "Gwoup separe" },
    itemDeleted: { en: "Item deleted", es: "Item eliminado", pt: "Item excluído", ht: "Eleman efase" },
    canvasConfigUpdated: { en: "Canvas configuration updated", es: "Configuración de canvas actualizada", pt: "Configuração do canvas atualizada", ht: "Konfigirasyon canvas mete ajou" },
    nameUpdated: { en: "Name updated", es: "Nombre actualizado", pt: "Nome atualizado", ht: "Non mete ajou" },
    changeUndone: { en: "Change undone", es: "Cambio deshecho", pt: "Alteração desfeita", ht: "Chanjman defèt" },
    changeRedone: { en: "Change redone", es: "Cambio rehecho", pt: "Alteração refeita", ht: "Chanjman refèt" },
    // UI elements
    cancel: { en: "Cancel", es: "Cancelar", pt: "Cancelar", ht: "Anile" },
    save: { en: "Save", es: "Guardar", pt: "Salvar", ht: "Anrejistre" },
    productName: { en: "Product name", es: "Nombre del producto", pt: "Nome do produto", ht: "Non pwodwi a" },
    price: { en: "Price", es: "Precio", pt: "Preço", ht: "Pri" },
    name: { en: "Name", es: "Nombre", pt: "Nome", ht: "Non" },
    videoName: { en: "Video name...", es: "Nombre del video...", pt: "Nome do vídeo...", ht: "Non videyo..." },
    shapeName: { en: "Shape name...", es: "Nombre de la forma...", pt: "Nome da forma...", ht: "Non fòm nan..." },
    imageName: { en: "Image name...", es: "Nombre de la imagen...", pt: "Nome da imagem...", ht: "Non imaj la..." },
    text: { en: "Text", es: "Texto", pt: "Texto", ht: "Tèks" },
    image: { en: "Image", es: "Imagen", pt: "Imagem", ht: "Imaj" },
    video: { en: "Video", es: "Video", pt: "Vídeo", ht: "Videyo" },
    element: { en: "Element", es: "Elemento", pt: "Elemento", ht: "Eleman" },
    shadow: { en: "Shadow", es: "Sombra", pt: "Sombra", ht: "Lonbraj" },
    pos: { en: "Pos", es: "Pos", pt: "Pos", ht: "Poz" },
  };
  return messages[key]?.[language] || messages[key]?.en || key;
};

const FONTS = [
  { value: "Inter", label: "Inter" },
  { value: "Manrope", label: "Manrope" },
  { value: "Playfair Display", label: "Playfair Display" },
  { value: "Oswald", label: "Oswald" },
  { value: "Caveat", label: "Caveat" },
  { value: "Merriweather", label: "Merriweather" },
  { value: "Lato", label: "Lato" },
];

const FONT_WEIGHTS = [
  { value: 300, label: "Light" },
  { value: 400, label: "Normal" },
  { value: 500, label: "Medium" },
  { value: 600, label: "Semi Bold" },
  { value: 700, label: "Bold" },
];

// Animation presets for images and elements
const ANIMATIONS = [
  { value: "none", label: "Sin animación", icon: "⏹️" },
  { value: "float", label: "Flotar", icon: "🎈" },
  { value: "float-slow", label: "Flotar (lento)", icon: "🎈" },
  { value: "pulse", label: "Pulso", icon: "💓" },
  { value: "pulse-slow", label: "Pulso (lento)", icon: "💓" },
  { value: "bounce", label: "Rebote", icon: "⚽" },
  { value: "swing", label: "Balanceo", icon: "🎭" },
  { value: "swing-slow", label: "Balanceo (lento)", icon: "🎭" },
  { value: "fade", label: "Desvanecimiento", icon: "👻" },
  { value: "shake", label: "Vibración", icon: "📳" },
  { value: "zoom", label: "Zoom", icon: "🔍" },
  { value: "slide-x", label: "Deslizar H", icon: "↔️" },
  { value: "slide-y", label: "Deslizar V", icon: "↕️" },
  { value: "glow", label: "Brillo", icon: "✨" },
  { value: "rotate", label: "Rotación", icon: "🔄" },
  { value: "rotate-slow", label: "Rotación (lenta)", icon: "🔄" },
  { value: "flip-x", label: "Voltear H", icon: "🔀" },
  { value: "flip-y", label: "Voltear V", icon: "🔃" },
];

// Entry animations for when page loads
const ENTRY_ANIMATIONS = [
  { value: "none", label: "Sin entrada", icon: "⏹️" },
  { value: "entry-fade", label: "Aparecer", icon: "👁️" },
  { value: "entry-slide-up", label: "Desde abajo", icon: "⬆️" },
  { value: "entry-slide-down", label: "Desde arriba", icon: "⬇️" },
  { value: "entry-slide-left", label: "Desde derecha", icon: "⬅️" },
  { value: "entry-slide-right", label: "Desde izquierda", icon: "➡️" },
  { value: "entry-scale", label: "Escala", icon: "📐" },
  { value: "entry-rotate", label: "Rotar entrada", icon: "🌀" },
];

// RSS Feed presets
const RSS_PRESETS = [
  { key: "custom", label: "URL Personalizada" },
  { key: "bbc", label: "BBC News (EN)" },
  { key: "bbc_mundo", label: "BBC Mundo (ES)" },
  { key: "cnn", label: "CNN (EN)" },
  { key: "cnn_espanol", label: "CNN Español (ES)" },
  { key: "reuters", label: "Reuters (EN)" },
  { key: "nyt", label: "New York Times (EN)" },
  { key: "elpais", label: "El País (ES)" },
  { key: "elmundo", label: "El Mundo (ES)" },
];

// Shape types available
const SHAPE_TYPES = [
  { value: "rectangle", label: "Rectángulo", icon: "⬜" },
  { value: "circle", label: "Círculo", icon: "⭕" },
  { value: "triangle", label: "Triángulo", icon: "🔺" },
  { value: "star", label: "Estrella", icon: "⭐" },
  { value: "hexagon", label: "Hexágono", icon: "⬡" },
  { value: "line", label: "Línea", icon: "➖" },
  { value: "arrow", label: "Flecha", icon: "➡️" },
];

const CANVAS_PRESETS = [
  { label: "16:9 (1920x1080)", width: 1920, height: 1080 },
  { label: "4:3 (1024x768)", width: 1024, height: 768 },
  { label: "1:1 Cuadrado (1080x1080)", width: 1080, height: 1080 },
  { label: "Vertical 9:16 (1080x1920)", width: 1080, height: 1920 },
  { label: "Personalizado", width: 0, height: 0 },
];

// DraggableMenuItem component with exact positioning, scaled font, z-index, and resize handles for images
const DraggableMenuItem = ({ item, isSelected, onDragStop, onClick, onResize, canvasSize, canvasConfig, isPanMode, selectedCount }) => {
  const nodeRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeStart, setResizeStart] = useState({ x: 0, y: 0, width: 0, height: 0 });
  
  // Convert percentage to pixels for display (exact calculation)
  const pixelX = (item.x / 100) * canvasSize.width;
  const pixelY = (item.y / 100) * canvasSize.height;
  
  // Scale font size proportionally to canvas display size
  const scaleFactor = canvasSize.width / canvasConfig.width;
  const scaledFontSize = item.font_size * scaleFactor;

  // For images, videos, and shapes: scale width and height
  const isResizable = item.type === 'image' || item.type === 'video' || item.type === 'gif' || item.type === 'shape' || item.type === 'clock' || item.type === 'weather' || item.type === 'ticker' || item.type === 'rss';
  const scaledWidth = isResizable ? (item.width / 100) * canvasSize.width : 'auto';
  const scaledHeight = isResizable ? (item.height / 100) * canvasSize.height : 'auto';

  // Text shadow style
  const textShadowStyle = item.text_shadow 
    ? '2px 2px 4px rgba(0,0,0,0.8), 1px 1px 2px rgba(0,0,0,0.6)' 
    : 'none';

  // Handle resize start
  const handleResizeStart = (e, corner) => {
    e.stopPropagation();
    e.preventDefault();
    setIsResizing(true);
    setResizeStart({
      x: e.clientX,
      y: e.clientY,
      width: item.width,
      height: item.height,
      corner
    });
    
    const handleMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - e.clientX;
      const deltaY = moveEvent.clientY - e.clientY;
      
      // Convert pixel delta to percentage
      const deltaWidthPercent = (deltaX / canvasSize.width) * 100;
      const deltaHeightPercent = (deltaY / canvasSize.height) * 100;
      
      let newWidth = item.width;
      let newHeight = item.height;
      
      if (corner.includes('e')) newWidth = Math.max(2, item.width + deltaWidthPercent);
      if (corner.includes('w')) newWidth = Math.max(2, item.width - deltaWidthPercent);
      if (corner.includes('s')) newHeight = Math.max(2, item.height + deltaHeightPercent);
      if (corner.includes('n')) newHeight = Math.max(2, item.height - deltaHeightPercent);
      
      // For corner handles, maintain aspect ratio with shift key
      if (corner.length === 2 && moveEvent.shiftKey) {
        const aspectRatio = item.width / item.height;
        if (Math.abs(deltaWidthPercent) > Math.abs(deltaHeightPercent)) {
          newHeight = newWidth / aspectRatio;
        } else {
          newWidth = newHeight * aspectRatio;
        }
      }
      
      onResize(item.id, newWidth, newHeight);
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Resize handle component
  const ResizeHandle = ({ position, cursor }) => (
    <div
      className="absolute bg-blue-500 border border-white z-50 resize-handle"
      style={{
        width: 10,
        height: 10,
        cursor: cursor,
        ...getHandlePosition(position),
      }}
      onMouseDown={(e) => handleResizeStart(e, position)}
    />
  );
  
  const getHandlePosition = (position) => {
    const styles = {
      nw: { top: -5, left: -5 },
      ne: { top: -5, right: -5 },
      sw: { bottom: -5, left: -5 },
      se: { bottom: -5, right: -5 },
      n: { top: -5, left: '50%', transform: 'translateX(-50%)' },
      s: { bottom: -5, left: '50%', transform: 'translateX(-50%)' },
      e: { right: -5, top: '50%', transform: 'translateY(-50%)' },
      w: { left: -5, top: '50%', transform: 'translateY(-50%)' },
    };
    return styles[position];
  };

  return (
    <Draggable
      nodeRef={nodeRef}
      position={{ x: pixelX, y: pixelY }}
      onStop={(e, data) => onDragStop(item.id, e, data)}
      bounds="parent"
      disabled={isPanMode || isResizing}
      cancel=".resize-handle"
    >
      <div
        ref={nodeRef}
        className={`absolute rounded transition-all ${
          isSelected ? 'ring-2 ring-blue-500 bg-blue-500/20' : 'hover:bg-white/10'
        } ${isPanMode ? 'cursor-grab' : 'cursor-move'}`}
        style={{ 
          padding: 0,
          zIndex: item.z_index || 0,
          opacity: item.opacity ?? 1,
          ...(isResizable ? { width: scaledWidth, height: scaledHeight } : {}),
        }}
        onClick={(e) => {
          if (!isPanMode) {
            e.stopPropagation();
            onClick(item, e); // Pass the event for Shift detection
          }
        }}
        data-testid={`menu-item-${item.id}`}
      >
        {item.type === 'image' ? (
          <img
            src={item.image_url}
            alt={item.label || 'Image'}
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              pointerEvents: 'none',
            }}
            draggable={false}
          />
        ) : item.type === 'video' || item.type === 'gif' ? (
          // Video/GIF rendering - all content has pointerEvents: none so parent handles clicks
          <>
            {item.video_type === 'youtube' ? (
              <div 
                className="flex items-center justify-center bg-black/50 rounded border-2 border-red-500/50"
                style={{ 
                  width: '100%', 
                  height: '100%',
                  pointerEvents: 'none',
                  minWidth: 50,
                  minHeight: 30,
                }}
              >
                <Film className="w-8 h-8 text-white opacity-70" />
                <span className="text-white text-sm ml-2 font-medium">YouTube</span>
              </div>
            ) : item.video_type === 'vimeo' ? (
              <div 
                className="flex items-center justify-center bg-blue-900/50 rounded border-2 border-blue-400/50"
                style={{ 
                  width: '100%', 
                  height: '100%',
                  pointerEvents: 'none',
                  minWidth: 50,
                  minHeight: 30,
                }}
              >
                <Film className="w-8 h-8 text-white opacity-70" />
                <span className="text-white text-sm ml-2 font-medium">Vimeo</span>
              </div>
            ) : item.video_type === 'direct_url' ? (
              <div 
                className="flex items-center justify-center bg-green-900/50 rounded border-2 border-green-400/50"
                style={{ 
                  width: '100%', 
                  height: '100%',
                  pointerEvents: 'none',
                  minWidth: 50,
                  minHeight: 30,
                }}
              >
                <Film className="w-8 h-8 text-white opacity-70" />
                <span className="text-white text-sm ml-2 font-medium">Video URL</span>
              </div>
            ) : item.video_type === 'gif' ? (
              <img
                src={item.video_url}
                alt={item.label || 'GIF'}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  pointerEvents: 'none',
                }}
                draggable={false}
              />
            ) : (
              <video
                src={item.video_url}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  pointerEvents: 'none',
                }}
                muted={item.video_muted !== false}
                loop={item.video_loop !== false}
                autoPlay={item.video_autoplay !== false}
                playsInline
              />
            )}
          </>
        ) : item.type === 'shape' ? (
          // Shape rendering
          <div
            style={{
              width: scaledWidth,
              height: scaledHeight,
              backgroundColor: item.shape_type === 'triangle' ? 'transparent' : (item.shape_fill || '#3b82f6'),
              border: item.shape_stroke_width > 0 && item.shape_type !== 'triangle' 
                ? `${item.shape_stroke_width}px solid ${item.shape_stroke || '#1e40af'}` 
                : 'none',
              borderRadius: item.shape_type === 'circle' ? '50%' : `${item.shape_border_radius || 0}px`,
              transform: [
                item.shape_rotation ? `rotate(${item.shape_rotation}deg)` : '',
                item.shape_skew_x ? `skewX(${item.shape_skew_x}deg)` : '',
                item.shape_skew_y ? `skewY(${item.shape_skew_y}deg)` : '',
              ].filter(Boolean).join(' ') || undefined,
              pointerEvents: 'none',
              // Triangle using CSS borders
              ...(item.shape_type === 'triangle' ? {
                width: 0,
                height: 0,
                backgroundColor: 'transparent',
                borderLeft: `${scaledWidth / 2}px solid transparent`,
                borderRight: `${scaledWidth / 2}px solid transparent`,
                borderBottom: `${scaledHeight}px solid ${item.shape_fill || '#3b82f6'}`,
              } : {}),
            }}
          />
        ) : item.type === 'clock' ? (
          // Clock widget preview in editor
          <div
            className="flex items-center justify-center bg-gray-800/80 rounded"
            style={{ 
              width: scaledWidth, 
              height: scaledHeight,
              pointerEvents: 'none',
              minWidth: 80,
              minHeight: 40,
              color: item.color || '#ffffff',
              fontFamily: item.font_family || 'Inter',
            }}
          >
            <div className="text-center">
              <div style={{ fontSize: `${scaledFontSize}px`, fontWeight: 700 }}>12:00</div>
              {item.clock_show_date && (
                <div style={{ fontSize: `${scaledFontSize * 0.4}px`, opacity: 0.7 }}>Fecha</div>
              )}
            </div>
          </div>
        ) : item.type === 'weather' ? (
          // Weather widget preview in editor
          <div
            className="flex items-center justify-center bg-sky-800/80 rounded"
            style={{ 
              width: scaledWidth, 
              height: scaledHeight,
              pointerEvents: 'none',
              minWidth: 80,
              minHeight: 50,
              color: item.color || '#ffffff',
            }}
          >
            <div className="text-center">
              <div style={{ fontSize: `${scaledFontSize}px`, fontWeight: 700 }}>🌤️ 22°</div>
              <div style={{ fontSize: `${scaledFontSize * 0.4}px`, opacity: 0.7 }}>{item.weather_city || 'Ciudad'}</div>
            </div>
          </div>
        ) : item.type === 'ticker' ? (
          // Ticker widget preview in editor
          <div
            className="flex items-center overflow-hidden rounded"
            style={{ 
              width: scaledWidth, 
              height: scaledHeight,
              pointerEvents: 'none',
              backgroundColor: item.ticker_bg_color || '#000000',
              color: item.ticker_text_color || '#ffffff',
              minWidth: 100,
              minHeight: 30,
            }}
          >
            <div 
              className="whitespace-nowrap px-2"
              style={{ fontSize: `${scaledFontSize * 0.8}px` }}
            >
              📢 {item.ticker_text?.substring(0, 30) || 'Ticker de noticias...'}
            </div>
          </div>
        ) : item.type === 'rss' ? (
          // RSS widget preview in editor
          <div
            className="flex items-center justify-center bg-orange-800/80 rounded"
            style={{ 
              width: scaledWidth, 
              height: scaledHeight,
              pointerEvents: 'none',
              minWidth: 100,
              minHeight: 60,
              color: item.color || '#ffffff',
            }}
          >
            <div className="text-center p-2">
              <div style={{ fontSize: `${scaledFontSize * 0.6}px`, fontWeight: 600 }}>📰 RSS Feed</div>
              <div style={{ fontSize: `${scaledFontSize * 0.4}px`, opacity: 0.7 }}>
                {item.rss_preset || 'Noticias'}
              </div>
            </div>
          </div>
        ) : (
          <div
            className="menu-item-text whitespace-nowrap"
            style={{
              fontFamily: item.font_family,
              fontSize: `${scaledFontSize}px`,
              fontWeight: item.font_weight,
              color: item.color,
              lineHeight: 1.2,
              textShadow: textShadowStyle,
            }}
          >
            {item.text}
          </div>
        )}
        
        {/* Resize handles for images, videos, shapes and widgets */}
        {isSelected && isResizable && !isPanMode && (
          <>
            <ResizeHandle position="nw" cursor="nw-resize" />
            <ResizeHandle position="ne" cursor="ne-resize" />
            <ResizeHandle position="sw" cursor="sw-resize" />
            <ResizeHandle position="se" cursor="se-resize" />
            <ResizeHandle position="n" cursor="n-resize" />
            <ResizeHandle position="s" cursor="s-resize" />
            <ResizeHandle position="e" cursor="e-resize" />
            <ResizeHandle position="w" cursor="w-resize" />
          </>
        )}
      </div>
    </Draggable>
  );
};

const EditorPage = () => {
  const { menuId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const { t, language } = useLanguage();
  const canvasRef = useRef(null);
  const fileInputRef = useRef(null);
  const imageInputRef = useRef(null); // For adding images to canvas

  const [menu, setMenu] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]); // Array for multi-selection
  const [isAddItemOpen, setIsAddItemOpen] = useState(false);
  const [isCanvasSettingsOpen, setIsCanvasSettingsOpen] = useState(false);
  const [newItemType, setNewItemType] = useState("name");
  const [newItemText, setNewItemText] = useState("");
  const [canvasSize, setCanvasSize] = useState({ width: 800, height: 450 });
  const [canvasConfig, setCanvasConfig] = useState({ width: 1920, height: 1080 });
  const [tempCanvasConfig, setTempCanvasConfig] = useState({ width: 1920, height: 1080 });
  const [showGrid, setShowGrid] = useState(false);
  const [showRulers, setShowRulers] = useState(false);
  const [zoom, setZoom] = useState(100); // Zoom percentage
  const [gridSize, setGridSize] = useState(10); // Grid size in percentage
  const [isPanMode, setIsPanMode] = useState(false); // Pan mode for moving canvas
  const [panOffset, setPanOffset] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  // Media Library
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [libraryMode, setLibraryMode] = useState("all"); // "image" | "video" | "all"
  const [libraryPurpose, setLibraryPurpose] = useState("insert"); // "insert" | "background"
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [refreshInterval, setRefreshInterval] = useState(10);
  const [tempRefreshInterval, setTempRefreshInterval] = useState(10);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState("");
  
  // Clipboard state
  const [clipboard, setClipboard] = useState(null);
  
  // History state for undo/redo
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const [isUndoRedo, setIsUndoRedo] = useState(false);

  // Drawing mode state - for placing objects with mouse drag
  const [isDrawingMode, setIsDrawingMode] = useState(false);
  const [drawingType, setDrawingType] = useState(null); // "shape", "image", "widget", etc.
  const [drawingConfig, setDrawingConfig] = useState(null); // Config for the item being drawn
  const [drawingStart, setDrawingStart] = useState(null); // Start position {x, y} in percentage
  const [drawingCurrent, setDrawingCurrent] = useState(null); // Current position during draw
  const [isDrawing, setIsDrawing] = useState(false); // Currently dragging to draw
  
  // Spacebar pan mode (temporary)
  const [isSpacePressed, setIsSpacePressed] = useState(false);
  const [previousPanMode, setPreviousPanMode] = useState(false);

  // Video dialog state
  const [isAddVideoOpen, setIsAddVideoOpen] = useState(false);
  const [videoSource, setVideoSource] = useState("upload"); // "upload", "youtube", "vimeo", "gif"
  const [videoUrl, setVideoUrl] = useState("");
  const [videoMuted, setVideoMuted] = useState(true);
  const [videoLoop, setVideoLoop] = useState(true);
  const [videoAutoplay, setVideoAutoplay] = useState(true);
  const [isUploadingVideo, setIsUploadingVideo] = useState(false);

  // Widget dialog states
  const [isAddWidgetOpen, setIsAddWidgetOpen] = useState(false);
  const [widgetType, setWidgetType] = useState("clock"); // "clock", "weather", "ticker", "rss"
  // Clock widget state
  const [clockFormat, setClockFormat] = useState("12h");
  const [clockShowDate, setClockShowDate] = useState(true);
  const [clockShowSeconds, setClockShowSeconds] = useState(false);
  // Weather widget state
  const [weatherCity, setWeatherCity] = useState("");
  const [weatherApiKey, setWeatherApiKey] = useState("");
  const [weatherUnits, setWeatherUnits] = useState("metric");
  // Ticker widget state
  const [tickerText, setTickerText] = useState("");
  const [tickerSpeed, setTickerSpeed] = useState(30);
  const [tickerBgColor, setTickerBgColor] = useState("#000000");
  const [tickerTextColor, setTickerTextColor] = useState("#ffffff");
  // RSS widget state
  const [rssPreset, setRssPreset] = useState("");
  const [rssUrl, setRssUrl] = useState("");
  const [rssMaxItems, setRssMaxItems] = useState(5);

  // Shape dialog state
  const [isAddShapeOpen, setIsAddShapeOpen] = useState(false);
  const [shapeType, setShapeType] = useState("rectangle");
  const [shapeFill, setShapeFill] = useState("#3b82f6");
  const [shapeStroke, setShapeStroke] = useState("#1e40af");
  const [shapeStrokeWidth, setShapeStrokeWidth] = useState(0);
  const [shapeBorderRadius, setShapeBorderRadius] = useState(0);

  // Direct video URL state
  const [directVideoUrl, setDirectVideoUrl] = useState("");

  const GRID_SIZES = [1, 3, 5, 10, 20, 25, 50];
  const ZOOM_LEVELS = [25, 50, 75, 100, 150, 200, 300, 400, 500, 750, 1000];
  const REFRESH_OPTIONS = [5, 10, 15, 30, 60];

  const fetchMenu = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/menus/${menuId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMenu(response.data);
      if (response.data.canvas_width && response.data.canvas_height) {
        setCanvasConfig({ 
          width: response.data.canvas_width, 
          height: response.data.canvas_height 
        });
        setTempCanvasConfig({ 
          width: response.data.canvas_width, 
          height: response.data.canvas_height 
        });
      }
      if (response.data.refresh_interval) {
        setRefreshInterval(response.data.refresh_interval);
        setTempRefreshInterval(response.data.refresh_interval);
      }
    } catch (error) {
      toast.error(getToastMsg(language, 'errorLoadingMenu'));
      navigate("/");
    } finally {
      setIsLoading(false);
    }
    // `language` is intentionally not in deps: changing language should not refetch menu data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuId, token, navigate]);

  useEffect(() => {
    fetchMenu();
  }, [fetchMenu]);

  // Calculate base canvas size (before zoom)
  const [baseCanvasSize, setBaseCanvasSize] = useState({ width: 800, height: 450 });
  
  useEffect(() => {
    const updateCanvasSize = () => {
      if (!canvasRef.current) return;
      
      const container = canvasRef.current.parentElement;
      const maxWidth = container.clientWidth - 48;
      const maxHeight = window.innerHeight - 200;
      
      const aspectRatio = canvasConfig.width / canvasConfig.height;
      
      let width, height;
      
      if (maxWidth / maxHeight > aspectRatio) {
        height = maxHeight;
        width = height * aspectRatio;
      } else {
        width = maxWidth;
        height = width / aspectRatio;
      }
      
      setBaseCanvasSize({ width: Math.floor(width), height: Math.floor(height) });
    };

    updateCanvasSize();
    window.addEventListener('resize', updateCanvasSize);
    return () => window.removeEventListener('resize', updateCanvasSize);
  }, [canvasConfig]);

  // Apply zoom to canvas size
  useEffect(() => {
    const zoomFactor = zoom / 100;
    setCanvasSize({
      width: Math.floor(baseCanvasSize.width * zoomFactor),
      height: Math.floor(baseCanvasSize.height * zoomFactor)
    });
  }, [baseCanvasSize, zoom]);

  // Spacebar to temporarily enable pan mode
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore if typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      
      if (e.code === 'Space' && !isSpacePressed) {
        e.preventDefault();
        setIsSpacePressed(true);
        setPreviousPanMode(isPanMode);
        setIsPanMode(true);
      }
    };

    const handleKeyUp = (e) => {
      if (e.code === 'Space' && isSpacePressed) {
        e.preventDefault();
        setIsSpacePressed(false);
        setIsPanMode(previousPanMode);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isSpacePressed, isPanMode, previousPanMode]);

  // Cancel drawing mode on Escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isDrawingMode) {
        setIsDrawingMode(false);
        setDrawingType(null);
        setDrawingConfig(null);
        setDrawingStart(null);
        setDrawingCurrent(null);
        setIsDrawing(false);
        toast.info(getToastMsg(language, 'drawModeCanceled'));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // `language` referenced inside handleKeyDown via getToastMsg — intentionally stable per session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDrawingMode]);

  // Zoom controls
  const handleZoomIn = () => {
    const currentIndex = ZOOM_LEVELS.indexOf(zoom);
    if (currentIndex < ZOOM_LEVELS.length - 1) {
      setZoom(ZOOM_LEVELS[currentIndex + 1]);
    } else if (zoom < 1000) {
      setZoom(Math.min(zoom + 50, 1000));
    }
  };

  const handleZoomOut = () => {
    const currentIndex = ZOOM_LEVELS.indexOf(zoom);
    if (currentIndex > 0) {
      setZoom(ZOOM_LEVELS[currentIndex - 1]);
    } else if (zoom > 25) {
      setZoom(Math.max(zoom - 25, 25));
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      // Compress then upload to backend (disk)
      const { dataUrl, sizeKB } = await compressImage(file, {
        maxWidth: 2400,
        maxHeight: 2400,
        quality: 0.85,
      });
      const blob = await (await fetch(dataUrl)).blob();
      const form = new FormData();
      form.append("file", blob, file.name);
      const response = await axios.post(`${API}/upload`, form, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setMenu(prev => ({ ...prev, background_image: response.data.url }));
      toast.success(`${getToastMsg(language, 'imageLoaded')} (${sizeKB} KB)`);
    } catch (error) {
      toast.error(getToastMsg(language, 'errorLoadingImage'));
    } finally {
      e.target.value = '';
    }
  };

  // Open library for selecting media
  const openLibrary = (purpose, mode = "all") => {
    setLibraryPurpose(purpose);
    setLibraryMode(mode);
    setIsLibraryOpen(true);
  };

  // Use a media item from the library (insert to canvas or as background)
  const handleUseMedia = (media) => {
    if (!media || !media.url) return;

    if (libraryPurpose === "background") {
      setMenu(prev => ({ ...prev, background_image: media.url }));
      toast.success(getToastMsg(language, 'imageLoaded'));
      return;
    }

    // Insert as new canvas item
    if (media.type === "image") {
      const img = new window.Image();
      img.onload = () => {
        const aspectRatio = img.height / img.width;
        const widthPercent = 20;
        const heightPercent = widthPercent * aspectRatio * (canvasConfig.width / canvasConfig.height);
        const item = {
          id: `img-${Date.now()}`,
          type: 'image',
          text: '',
          label: media.original_name?.replace(/\.[^/.]+$/, "") || "image",
          x: 5, y: 5,
          width: widthPercent,
          height: heightPercent,
          image_url: media.url,
          z_index: getMaxZIndex() + 1,
          opacity: 1,
        };
        setMenu(prev => ({ ...prev, items: [...(prev.items || []), item] }));
        toast.success(t('imageAdded'));
      };
      img.src = media.url.startsWith("http") ? media.url : `${process.env.REACT_APP_BACKEND_URL}${media.url}`;
    } else {
      // video or gif
      const isGif = media.type === "gif";
      const item = {
        id: `video-${Date.now()}`,
        type: isGif ? 'gif' : 'video',
        text: '',
        label: media.original_name?.replace(/\.[^/.]+$/, "") || "video",
        x: 5, y: 5,
        width: 30, height: 20,
        video_url: media.url,
        video_type: isGif ? 'gif' : 'upload',
        video_muted: true,
        video_loop: true,
        video_autoplay: true,
        z_index: getMaxZIndex() + 1,
        opacity: 1,
      };
      setMenu(prev => ({ ...prev, items: [...(prev.items || []), item] }));
      toast.success(isGif ? getToastMsg(language, 'gifAdded') : getToastMsg(language, 'videoAdded'));
    }
  };

  const handleSaveMenu = async () => {
    if (!menu) return;
    setIsSaving(true);

    try {
      await axios.put(
        `${API}/menus/${menuId}`,
        {
          name: menu.name,
          background_image: menu.background_image,
          items: menu.items,
          canvas_width: canvasConfig.width,
          canvas_height: canvasConfig.height,
          refresh_interval: refreshInterval
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(t('menuSaved'));
    } catch (error) {
      toast.error(getToastMsg(language, 'errorSavingMenu'));
    } finally {
      setIsSaving(false);
    }
  };

  // Export menu as image
  const [isExporting, setIsExporting] = useState(false);
  
  const handleExportImage = async (format = 'png') => {
    if (!canvasRef.current) return;
    
    setIsExporting(true);
    try {
      // Temporarily hide selection and grid for clean export
      const originalZoom = zoom;
      const originalShowGrid = showGrid;
      setZoom(100);
      setShowGrid(false);
      setSelectedItems([]);
      
      // Wait for state updates
      await new Promise(resolve => setTimeout(resolve, 100));
      
      const canvas = await html2canvas(canvasRef.current, {
        backgroundColor: null,
        scale: 2, // Higher quality
        useCORS: true,
        allowTaint: true,
      });
      
      // Restore original state
      setZoom(originalZoom);
      setShowGrid(originalShowGrid);
      
      // Download
      const link = document.createElement('a');
      link.download = `${menu?.name || 'menu'}.${format}`;
      link.href = canvas.toDataURL(`image/${format}`, format === 'jpg' ? 0.95 : 1);
      link.click();
      
      toast.success(t('imageExported'));
    } catch (error) {
      console.error('Export error:', error);
      toast.error(getToastMsg(language, 'errorExportingImage'));
    } finally {
      setIsExporting(false);
    }
  };

  // Get max z-index from items
  const getMaxZIndex = () => {
    if (!menu?.items?.length) return 0;
    return Math.max(...menu.items.map(item => item.z_index || 0));
  };

  const handleAddItem = async () => {
    if (!newItemText.trim()) {
      toast.error(getToastMsg(language, 'enterText'));
      return;
    }

    const item = {
      id: `item-${Date.now()}`,
      type: newItemType,
      text: newItemText,
      label: "",
      x: 5,
      y: 5,
      font_size: newItemType === "name" ? 16 : 14,
      font_family: "Inter",
      color: "#ffffff",
      font_weight: newItemType === "name" ? 600 : 400,
      text_shadow: false,
      z_index: getMaxZIndex() + 1,
      opacity: 1,
    };

    setMenu(prev => ({
      ...prev,
      items: [...(prev.items || []), item]
    }));

    setNewItemText("");
    setIsAddItemOpen(false);
    toast.success(t('itemAdded'));
  };

  // Add image to canvas: compress client-side, upload to backend (disk), use URL
  const handleAddImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const { dataUrl, width, height, sizeKB } = await compressImage(file, {
        maxWidth: 1920,
        maxHeight: 1920,
        quality: 0.85,
      });

      // Convert compressed data URL to Blob, upload to backend
      const blob = await (await fetch(dataUrl)).blob();
      const form = new FormData();
      form.append("file", blob, file.name);
      const uploadRes = await axios.post(`${API}/upload`, form, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      const imageUrl = uploadRes.data.url;

      const aspectRatio = height / width;
      const widthPercent = 20;
      const heightPercent = widthPercent * aspectRatio * (canvasConfig.width / canvasConfig.height);

      const item = {
        id: `img-${Date.now()}`,
        type: 'image',
        text: '',
        label: file.name.replace(/\.[^/.]+$/, ""),
        x: 5,
        y: 5,
        width: widthPercent,
        height: heightPercent,
        image_url: imageUrl,
        z_index: getMaxZIndex() + 1,
        opacity: 1,
      };

      setMenu(prev => ({
        ...prev,
        items: [...(prev.items || []), item]
      }));

      toast.success(`${t('imageAdded')} (${sizeKB} KB)`);
    } catch (err) {
      console.error("Image upload error:", err);
      toast.error(getToastMsg(language, 'errorLoadingImage'));
    } finally {
      e.target.value = '';
    }
  };

  // ==================== VIDEO/GIF FUNCTIONS ====================
  
  const handleAddVideoFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file type
    const allowedTypes = ['video/mp4', 'video/webm', 'video/quicktime', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(getToastMsg(language, 'unsupportedFormat'));
      return;
    }

    // Check file size (10MB limit)
    if (file.size > 10 * 1024 * 1024) {
      toast.error(getToastMsg(language, 'fileTooLarge'));
      return;
    }

    setIsUploadingVideo(true);

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('muted', videoMuted);

      const response = await axios.post(`${API}/upload/video`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      const isGif = file.type === 'image/gif';
      const item = {
        id: `video-${Date.now()}`,
        type: isGif ? 'gif' : 'video',
        text: '',
        label: file.name.replace(/\.[^/.]+$/, ""),
        x: 5,
        y: 5,
        width: 30,
        height: 20,
        video_url: response.data.url,
        video_type: response.data.video_type,
        video_muted: videoMuted,
        video_loop: videoLoop,
        video_autoplay: videoAutoplay,
        z_index: getMaxZIndex() + 1,
        opacity: 1,
      };

      setMenu(prev => ({
        ...prev,
        items: [...(prev.items || []), item]
      }));

      setIsAddVideoOpen(false);
      resetVideoDialog();
      toast.success(isGif ? getToastMsg(language, 'gifAdded') : getToastMsg(language, 'videoAdded'));
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || getToastMsg(language, 'errorUploadingVideo'));
    } finally {
      setIsUploadingVideo(false);
      e.target.value = '';
    }
  };

  const extractYouTubeId = (url) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const extractVimeoId = (url) => {
    const regExp = /vimeo\.com\/(\d+)/;
    const match = url.match(regExp);
    return match ? match[1] : null;
  };

  const handleAddExternalVideo = () => {
    if (!videoUrl.trim()) {
      toast.error(getToastMsg(language, 'enterValidUrl'));
      return;
    }

    let videoType = '';
    let embedUrl = '';

    if (videoSource === 'youtube') {
      const youtubeId = extractYouTubeId(videoUrl);
      if (!youtubeId) {
        toast.error(getToastMsg(language, 'invalidYoutubeUrl'));
        return;
      }
      videoType = 'youtube';
      embedUrl = `https://www.youtube.com/embed/${youtubeId}?autoplay=1&mute=${videoMuted ? 1 : 0}&loop=${videoLoop ? 1 : 0}&playlist=${youtubeId}`;
    } else if (videoSource === 'vimeo') {
      const vimeoId = extractVimeoId(videoUrl);
      if (!vimeoId) {
        toast.error(getToastMsg(language, 'invalidVimeoUrl'));
        return;
      }
      videoType = 'vimeo';
      embedUrl = `https://player.vimeo.com/video/${vimeoId}?autoplay=1&muted=${videoMuted ? 1 : 0}&loop=${videoLoop ? 1 : 0}`;
    }

    const item = {
      id: `video-${Date.now()}`,
      type: 'video',
      text: '',
      label: videoType === 'youtube' ? 'YouTube Video' : 'Vimeo Video',
      x: 5,
      y: 5,
      width: 30,
      height: 20,
      video_url: embedUrl,
      video_type: videoType,
      video_muted: videoMuted,
      video_loop: videoLoop,
      video_autoplay: videoAutoplay,
      z_index: getMaxZIndex() + 1,
      opacity: 1,
    };

    setMenu(prev => ({
      ...prev,
      items: [...(prev.items || []), item]
    }));

    setIsAddVideoOpen(false);
    resetVideoDialog();
    toast.success(`Video de ${videoType === 'youtube' ? 'YouTube' : 'Vimeo'} agregado`);
  };

  const resetVideoDialog = () => {
    setVideoSource("upload");
    setVideoUrl("");
    setVideoMuted(true);
    setVideoLoop(true);
    setVideoAutoplay(true);
  };

  // Add widget function
  const handleAddWidget = () => {
    let item = {
      id: `widget-${Date.now()}`,
      type: widgetType,
      text: '',
      label: '',
      x: 5,
      y: 5,
      font_size: 24,
      font_family: 'Inter',
      color: '#ffffff',
      font_weight: 600,
      z_index: getMaxZIndex() + 1,
      opacity: 1,
    };

    switch (widgetType) {
      case 'clock':
        item.label = 'Reloj';
        item.clock_format = clockFormat;
        item.clock_show_date = clockShowDate;
        item.clock_show_seconds = clockShowSeconds;
        item.width = 15;
        item.height = 8;
        break;
      case 'weather':
        if (!weatherCity || !weatherApiKey) {
          toast.error(getToastMsg(language, 'cityApiKeyRequired'));
          return;
        }
        item.label = `Clima - ${weatherCity}`;
        item.weather_city = weatherCity;
        item.weather_api_key = weatherApiKey;
        item.weather_units = weatherUnits;
        item.weather_show_icon = true;
        item.width = 15;
        item.height = 10;
        break;
      case 'ticker':
        if (!tickerText) {
          toast.error(getToastMsg(language, 'tickerTextRequired'));
          return;
        }
        item.label = 'Ticker de noticias';
        item.ticker_text = tickerText;
        item.ticker_speed = tickerSpeed;
        item.ticker_bg_color = tickerBgColor;
        item.ticker_text_color = tickerTextColor;
        item.width = 100;
        item.height = 5;
        item.x = 0;
        item.y = 95;
        break;
      case 'rss':
        if ((!rssPreset || rssPreset === 'custom') && !rssUrl) {
          toast.error(getToastMsg(language, 'selectPresetOrRss'));
          return;
        }
        item.label = (rssPreset && rssPreset !== 'custom') ? `RSS - ${RSS_PRESETS.find(p => p.key === rssPreset)?.label}` : 'RSS Feed';
        item.rss_preset = (rssPreset === 'custom') ? '' : rssPreset;
        item.rss_url = rssUrl;
        item.rss_max_items = rssMaxItems;
        item.rss_show_title = true;
        item.width = 30;
        item.height = 20;
        break;
      default:
        break;
    }

    setMenu(prev => ({
      ...prev,
      items: [...(prev.items || []), item]
    }));

    setIsAddWidgetOpen(false);
    resetWidgetDialog();
    toast.success(`Widget de ${widgetType === 'clock' ? 'Reloj' : widgetType === 'weather' ? 'Clima' : widgetType === 'ticker' ? 'Ticker' : 'RSS'} agregado`);
  };

  const resetWidgetDialog = () => {
    setWidgetType("clock");
    setClockFormat("12h");
    setClockShowDate(true);
    setClockShowSeconds(false);
    setWeatherCity("");
    setWeatherApiKey("");
    setWeatherUnits("metric");
    setTickerText("");
    setTickerSpeed(30);
    setTickerBgColor("#000000");
    setTickerTextColor("#ffffff");
    setRssPreset("");
    setRssUrl("");
    setRssMaxItems(5);
  };

  // Enter drawing mode for shape - user will click and drag to define size
  const handleAddShapeDrawMode = () => {
    const config = {
      type: 'shape',
      shape_type: shapeType,
      shape_fill: shapeFill,
      shape_stroke: shapeStroke,
      shape_stroke_width: shapeStrokeWidth,
      shape_border_radius: shapeBorderRadius,
      shape_rotation: 0,
      shape_skew_x: 0,
      shape_skew_y: 0,
      label: SHAPE_TYPES.find(s => s.value === shapeType)?.label || 'Forma',
      z_index: getMaxZIndex() + 1,
      opacity: 1,
    };

    setDrawingType('shape');
    setDrawingConfig(config);
    setIsDrawingMode(true);
    setIsAddShapeOpen(false);
    resetShapeDialog();
    toast.info(getToastMsg(language, 'clickDragToDrawShape'), { duration: 3000 });
  };

  // Add shape function (direct add without drawing)
  const handleAddShape = () => {
    const item = {
      id: `shape-${Date.now()}`,
      type: 'shape',
      shape_type: shapeType,
      shape_fill: shapeFill,
      shape_stroke: shapeStroke,
      shape_stroke_width: shapeStrokeWidth,
      shape_border_radius: shapeBorderRadius,
      shape_rotation: 0,
      shape_skew_x: 0,
      shape_skew_y: 0,
      label: SHAPE_TYPES.find(s => s.value === shapeType)?.label || 'Forma',
      x: 10,
      y: 10,
      width: 15,
      height: 15,
      z_index: getMaxZIndex() + 1,
      opacity: 1,
    };

    setMenu(prev => ({
      ...prev,
      items: [...(prev.items || []), item]
    }));

    setIsAddShapeOpen(false);
    resetShapeDialog();
    toast.success(`Forma "${item.label}" agregada`);
  };

  // Handle drawing on canvas
  const handleCanvasDrawStart = (e) => {
    if (!isDrawingMode || isPanMode) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    
    setDrawingStart({ x, y });
    setDrawingCurrent({ x, y });
    setIsDrawing(true);
  };

  const handleCanvasDrawMove = (e) => {
    if (!isDrawing || !drawingStart) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = Math.max(0, Math.min(100, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.max(0, Math.min(100, ((e.clientY - rect.top) / rect.height) * 100));
    
    setDrawingCurrent({ x, y });
  };

  const handleCanvasDrawEnd = () => {
    if (!isDrawing || !drawingStart || !drawingCurrent || !drawingConfig) {
      setIsDrawing(false);
      return;
    }
    
    // Calculate position and size
    const x = Math.min(drawingStart.x, drawingCurrent.x);
    const y = Math.min(drawingStart.y, drawingCurrent.y);
    const width = Math.abs(drawingCurrent.x - drawingStart.x);
    const height = Math.abs(drawingCurrent.y - drawingStart.y);
    
    // Minimum size check
    if (width < 2 || height < 2) {
      toast.error(getToastMsg(language, 'shapeTooSmall'));
      setIsDrawing(false);
      return;
    }
    
    // Create the item
    const item = {
      ...drawingConfig,
      id: `${drawingType}-${Date.now()}`,
      x,
      y,
      width,
      height,
    };
    
    setMenu(prev => ({
      ...prev,
      items: [...(prev.items || []), item]
    }));
    
    // Reset drawing state but stay in drawing mode for continuous drawing
    setIsDrawing(false);
    setDrawingStart(null);
    setDrawingCurrent(null);
    toast.success(`Forma creada (${width.toFixed(0)}% x ${height.toFixed(0)}%)`);
  };

  // Exit drawing mode
  const exitDrawingMode = () => {
    setIsDrawingMode(false);
    setDrawingType(null);
    setDrawingConfig(null);
    setDrawingStart(null);
    setDrawingCurrent(null);
    setIsDrawing(false);
  };

  const resetShapeDialog = () => {
    setShapeType("rectangle");
    setShapeFill("#3b82f6");
    setShapeStroke("#1e40af");
    setShapeStrokeWidth(0);
    setShapeBorderRadius(0);
  };

  // Add direct video URL function
  const handleAddDirectVideoUrl = () => {
    if (!directVideoUrl) {
      toast.error(getToastMsg(language, 'enterVideoUrl'));
      return;
    }

    const item = {
      id: `video-${Date.now()}`,
      type: 'video',
      video_url: directVideoUrl,
      video_type: 'direct_url',
      video_muted: videoMuted,
      video_loop: videoLoop,
      video_autoplay: videoAutoplay,
      label: 'Video URL',
      x: 10,
      y: 10,
      width: 30,
      height: 20,
      z_index: getMaxZIndex() + 1,
      opacity: 1,
    };

    setMenu(prev => ({
      ...prev,
      items: [...(prev.items || []), item]
    }));

    setIsAddVideoOpen(false);
    setDirectVideoUrl("");
    resetVideoDialog();
    toast.success(getToastMsg(language, 'videoAddedFromUrl'));
  };

  // Merge/Group selected items
  const handleMergeItems = () => {
    if (selectedItems.length < 2) {
      toast.error(getToastMsg(language, 'selectMinTwoElements'));
      return;
    }

    // Calculate bounding box of selected items
    const minX = Math.min(...selectedItems.map(i => i.x));
    const minY = Math.min(...selectedItems.map(i => i.y));
    const maxX = Math.max(...selectedItems.map(i => i.x + (i.width || 10)));
    const maxY = Math.max(...selectedItems.map(i => i.y + (i.height || 10)));

    const group = {
      id: `group-${Date.now()}`,
      type: 'group',
      label: `Grupo (${selectedItems.length} elementos)`,
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
      z_index: getMaxZIndex() + 1,
      opacity: 1,
      group_items: selectedItems.map(i => i.id),
    };

    // Add group and remove original items
    setMenu(prev => ({
      ...prev,
      items: [
        ...prev.items.filter(i => !selectedItems.find(s => s.id === i.id)),
        group,
        // Keep original items but mark them as grouped
        ...selectedItems.map(i => ({ ...i, _grouped: true, _group_id: group.id }))
      ]
    }));

    setSelectedItems([]);
    toast.success(`${selectedItems.length} elementos fusionados`);
  };

  // Ungroup items
  const handleUngroupItems = () => {
    const groupItem = selectedItems.find(i => i.type === 'group');
    if (!groupItem) {
      toast.error(getToastMsg(language, 'selectGroupToSeparate'));
      return;
    }

    setMenu(prev => ({
      ...prev,
      items: prev.items
        .filter(i => i.id !== groupItem.id)
        .map(i => {
          if (i._group_id === groupItem.id) {
            const { _grouped, _group_id, ...rest } = i;
            return rest;
          }
          return i;
        })
    }));

    setSelectedItems([]);
    toast.success(getToastMsg(language, 'groupSeparated'));
  };

  // Z-index layer functions
  const handleBringToFront = () => {
    if (selectedItems.length === 0) return;
    const maxZ = getMaxZIndex();
    selectedItems.forEach((item, index) => {
      handleUpdateItemById(item.id, 'z_index', maxZ + 1 + index);
    });
  };

  const handleSendToBack = () => {
    if (selectedItems.length === 0) return;
    const minZ = Math.min(...menu.items.map(item => item.z_index || 0));
    selectedItems.forEach((item, index) => {
      handleUpdateItemById(item.id, 'z_index', minZ - selectedItems.length + index);
    });
  };

  const handleBringForward = () => {
    if (selectedItems.length === 0) return;
    selectedItems.forEach(item => {
      handleUpdateItemById(item.id, 'z_index', (item.z_index || 0) + 1);
    });
  };

  const handleSendBackward = () => {
    if (selectedItems.length === 0) return;
    selectedItems.forEach(item => {
      handleUpdateItemById(item.id, 'z_index', (item.z_index || 0) - 1);
    });
  };

  // Helper to get the first selected item for property panel
  const selectedItem = selectedItems.length > 0 ? selectedItems[0] : null;

  const handleDragStop = (itemId, e, data) => {
    // Convert pixel position back to percentage (exact calculation)
    const xPercent = (data.x / canvasSize.width) * 100;
    const yPercent = (data.y / canvasSize.height) * 100;
    
    // Clamp values
    const clampedX = Math.max(0, Math.min(100, xPercent));
    const clampedY = Math.max(0, Math.min(100, yPercent));
    
    setMenu(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.id === itemId 
          ? { ...item, x: clampedX, y: clampedY }
          : item
      )
    }));
  };

  // Handle resize for images
  const handleResize = (itemId, newWidth, newHeight) => {
    setMenu(prev => ({
      ...prev,
      items: prev.items.map(item => 
        item.id === itemId 
          ? { ...item, width: newWidth, height: newHeight }
          : item
      )
    }));
    
    // Update selectedItems if it's one of the selected ones
    setSelectedItems(prev => prev.map(item => 
      item.id === itemId ? { ...item, width: newWidth, height: newHeight } : item
    ));
  };

  // Update a specific item by ID
  const handleUpdateItemById = (itemId, field, value) => {
    setMenu(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item.id === itemId
          ? { ...item, [field]: value }
          : item
      )
    }));

    setSelectedItems(prev => prev.map(item =>
      item.id === itemId ? { ...item, [field]: value } : item
    ));
  };

  const handleUpdateItem = (field, value) => {
    if (selectedItems.length === 0) return;

    // Update all selected items with the same property
    setMenu(prev => ({
      ...prev,
      items: prev.items.map(item =>
        selectedItems.some(sel => sel.id === item.id)
          ? { ...item, [field]: value }
          : item
      )
    }));

    setSelectedItems(prev => prev.map(item => ({ ...item, [field]: value })));
  };

  const handleDeleteItem = (itemId) => {
    setMenu(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== itemId)
    }));
    setSelectedItems(prev => prev.filter(item => item.id !== itemId));
    toast.success(getToastMsg(language, 'itemDeleted'));
  };

  // Delete all selected items
  const handleDeleteSelectedItems = () => {
    if (selectedItems.length === 0) return;
    const idsToDelete = selectedItems.map(item => item.id);
    setMenu(prev => ({
      ...prev,
      items: prev.items.filter(item => !idsToDelete.includes(item.id))
    }));
    setSelectedItems([]);
    toast.success(`${idsToDelete.length} item(s) eliminado(s)`);
  };

  const handleApplyCanvasConfig = () => {
    setCanvasConfig(tempCanvasConfig);
    setRefreshInterval(tempRefreshInterval);
    setIsCanvasSettingsOpen(false);
    toast.success(getToastMsg(language, 'canvasConfigUpdated'));
  };

  const handlePresetSelect = (preset) => {
    if (preset.width > 0) {
      setTempCanvasConfig({ width: preset.width, height: preset.height });
    }
  };

  // Pan mode handlers
  const handleCanvasMouseDown = (e) => {
    if (isPanMode && e.button === 0) {
      setIsPanning(true);
      setPanStart({ x: e.clientX - panOffset.x, y: e.clientY - panOffset.y });
      e.preventDefault();
    }
  };

  const handleCanvasMouseMove = (e) => {
    if (isPanning && isPanMode) {
      setPanOffset({
        x: e.clientX - panStart.x,
        y: e.clientY - panStart.y
      });
    }
  };

  const handleCanvasMouseUp = () => {
    setIsPanning(false);
  };

  const handleCanvasMouseLeave = () => {
    setIsPanning(false);
  };

  // Reset pan when zoom changes to 100%
  useEffect(() => {
    if (zoom === 100) {
      setPanOffset({ x: 0, y: 0 });
    }
  }, [zoom]);

  // Menu name editing
  const handleStartEditName = () => {
    setEditedName(menu?.name || "");
    setIsEditingName(true);
  };

  const handleSaveName = () => {
    if (editedName.trim()) {
      setMenu(prev => ({ ...prev, name: editedName.trim() }));
      setIsEditingName(false);
      toast.success(getToastMsg(language, 'nameUpdated'));
    }
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setEditedName("");
  };

  const handleNameKeyDown = (e) => {
    if (e.key === "Enter") {
      handleSaveName();
    } else if (e.key === "Escape") {
      handleCancelEditName();
    }
  };

  // ==================== HISTORY (UNDO/REDO) ====================
  
  // Save state to history whenever items change (but not during undo/redo)
  useEffect(() => {
    if (menu?.items && !isUndoRedo) {
      const itemsSnapshot = JSON.stringify(menu.items);
      const lastSnapshot = history[historyIndex] ? JSON.stringify(history[historyIndex]) : null;
      
      // Only save if items actually changed
      if (itemsSnapshot !== lastSnapshot) {
        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push(JSON.parse(itemsSnapshot));
        
        // Keep max 50 history entries
        if (newHistory.length > 50) {
          newHistory.shift();
        }
        
        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
      }
    }
    setIsUndoRedo(false);
    // Intentionally only tracking items mutation; history & isUndoRedo are written-to here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu?.items]);

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  const handleUndo = () => {
    if (canUndo) {
      setIsUndoRedo(true);
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      setMenu(prev => ({ ...prev, items: JSON.parse(JSON.stringify(history[newIndex])) }));
      setSelectedItems([]);
      toast.info(getToastMsg(language, 'changeUndone'));
    }
  };

  const handleRedo = () => {
    if (canRedo) {
      setIsUndoRedo(true);
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      setMenu(prev => ({ ...prev, items: JSON.parse(JSON.stringify(history[newIndex])) }));
      setSelectedItems([]);
      toast.info(getToastMsg(language, 'changeRedone'));
    }
  };

  // ==================== CLIPBOARD (CUT/COPY/PASTE) ====================
  
  const handleCopy = () => {
    if (selectedItems.length > 0) {
      // Copy all selected items without IDs
      setClipboard(selectedItems.map(item => ({ ...item, id: null })));
      toast.success(`${selectedItems.length} item(s) copiado(s)`);
    }
  };

  const handleCut = () => {
    if (selectedItems.length > 0) {
      // Copy all selected items without IDs
      setClipboard(selectedItems.map(item => ({ ...item, id: null })));
      // Remove all selected items
      const idsToRemove = selectedItems.map(item => item.id);
      setMenu(prev => ({
        ...prev,
        items: prev.items.filter(item => !idsToRemove.includes(item.id))
      }));
      setSelectedItems([]);
      toast.success(`${idsToRemove.length} item(s) cortado(s)`);
    }
  };

  const handlePaste = () => {
    if (clipboard && clipboard.length > 0) {
      const timestamp = Date.now();
      const newItems = clipboard.map((item, index) => ({
        ...item,
        id: `item-${timestamp}-${index}`,
        x: Math.min((item.x || 5) + 5, 90), // Offset slightly
        y: Math.min((item.y || 5) + 5, 90),
      }));
      setMenu(prev => ({
        ...prev,
        items: [...(prev.items || []), ...newItems]
      }));
      setSelectedItems(newItems);
      toast.success(`${newItems.length} item(s) pegado(s)`);
    }
  };

  // Keyboard shortcuts for clipboard and undo/redo
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Check if we're in an input field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const ctrlKey = isMac ? e.metaKey : e.ctrlKey;

      if (ctrlKey && e.key === 'c') {
        e.preventDefault();
        handleCopy();
      } else if (ctrlKey && e.key === 'x') {
        e.preventDefault();
        handleCut();
      } else if (ctrlKey && e.key === 'v') {
        e.preventDefault();
        handlePaste();
      } else if (ctrlKey && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          handleRedo();
        } else {
          handleUndo();
        }
      } else if (ctrlKey && e.key === 'y') {
        e.preventDefault();
        handleRedo();
      } else if (ctrlKey && e.key === 'a') {
        // Select all items
        e.preventDefault();
        if (menu?.items?.length > 0) {
          setSelectedItems([...menu.items]);
          toast.info(`${menu.items.length} items seleccionados`);
        }
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedItems.length > 0) {
          e.preventDefault();
          handleDeleteSelectedItems();
        }
      } else if (e.key === 'Escape') {
        // Deselect all
        setSelectedItems([]);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // Handlers are stable component-scope refs; deps cover the state they observe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItems, clipboard, canUndo, canRedo, menu?.items]);

  // Generate grid lines with configurable size
  const renderGrid = () => {
    if (!showGrid) return null;
    
    const lines = [];
    
    for (let i = gridSize; i < 100; i += gridSize) {
      // Vertical lines - cyan color
      lines.push(
        <div
          key={`v-${i}`}
          className="absolute top-0 bottom-0 border-l border-cyan-400/50"
          style={{ left: `${i}%` }}
        />
      );
      // Horizontal lines - cyan color
      lines.push(
        <div
          key={`h-${i}`}
          className="absolute left-0 right-0 border-t border-cyan-400/50"
          style={{ top: `${i}%` }}
        />
      );
    }
    
    return <div className="absolute inset-0 pointer-events-none">{lines}</div>;
  };

  // Render rulers with configurable intervals
  const renderRulers = () => {
    if (!showRulers) return null;
    
    const hMarks = [];
    const vMarks = [];
    const rulerInterval = gridSize; // Use same interval as grid
    
    for (let i = 0; i <= 100; i += rulerInterval) {
      hMarks.push(
        <div key={`hm-${i}`} className="absolute text-[8px] text-white/60" style={{ left: `${i}%`, top: '-16px', transform: 'translateX(-50%)' }}>
          {i}%
        </div>
      );
      vMarks.push(
        <div key={`vm-${i}`} className="absolute text-[8px] text-white/60" style={{ top: `${i}%`, left: '-24px', transform: 'translateY(-50%)' }}>
          {i}%
        </div>
      );
    }
    
    return (
      <>
        <div className="absolute top-0 left-0 right-0 h-4 bg-stone-700/80 -translate-y-full">
          {hMarks}
        </div>
        <div className="absolute top-0 left-0 bottom-0 w-6 bg-stone-700/80 -translate-x-full">
          {vMarks}
        </div>
      </>
    );
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="animate-pulse text-stone-600">Cargando editor...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100" data-testid="editor-page">
      {/* Top Toolbar - Simplified */}
      <div className="bg-white border-b border-stone-200 sticky top-0 z-20">
        <div className="max-w-full px-4 py-2 flex items-center justify-between">
          {/* Left section: Back + Name + Size */}
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={() => navigate("/")}
              className="h-8 px-2"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-1" />
              {t('back')}
            </Button>
            
            {/* Editable menu name */}
            {isEditingName ? (
              <div className="flex items-center gap-1">
                <Input
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onKeyDown={handleNameKeyDown}
                  className="h-7 w-44 text-sm font-medium"
                  autoFocus
                  data-testid="edit-menu-name-input"
                />
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-green-600 hover:bg-green-50"
                  onClick={handleSaveName}
                  data-testid="save-menu-name-btn"
                >
                  <Check className="w-3.5 h-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 text-stone-400 hover:bg-stone-100"
                  onClick={handleCancelEditName}
                  data-testid="cancel-menu-name-btn"
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            ) : (
              <div 
                className="flex items-center gap-1.5 group cursor-pointer hover:bg-stone-100 px-2 py-1 rounded transition-colors"
                onClick={handleStartEditName}
                data-testid="menu-name-clickable"
              >
                <span className="font-medium text-stone-800 text-sm">{menu?.name}</span>
                <Pencil className="w-3 h-3 text-stone-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            )}
            
            <span className="text-xs text-stone-400" data-testid="canvas-size-display">
              {canvasConfig.width} x {canvasConfig.height}px
            </span>
          </div>

          {/* Center section: Canvas tools */}
          <div className="flex items-center gap-1">
            {/* Zoom controls */}
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleZoomOut} disabled={zoom <= 25} data-testid="zoom-out-btn" title="Alejar">
              <ZoomOut className="w-4 h-4" />
            </Button>
            <span className="text-xs font-medium text-stone-600 min-w-[40px] text-center" data-testid="zoom-level">{zoom}%</span>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleZoomIn} disabled={zoom >= 1000} data-testid="zoom-in-btn" title="Acercar">
              <ZoomIn className="w-4 h-4" />
            </Button>

            <div className="h-5 w-px bg-stone-200 mx-1" />

            {/* Mode buttons */}
            <Button
              variant={!isPanMode && !isDrawingMode ? "secondary" : "ghost"}
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => { setIsPanMode(false); exitDrawingMode(); }}
              data-testid="select-mode-btn"
              title="Modo Selección"
            >
              <MousePointer2 className="w-4 h-4" />
            </Button>
            <Button
              variant={isPanMode && !isDrawingMode ? "secondary" : "ghost"}
              size="sm"
              className={`h-7 w-7 p-0 ${isSpacePressed ? 'bg-blue-100 ring-2 ring-blue-400' : ''}`}
              onClick={() => { setIsPanMode(true); exitDrawingMode(); }}
              data-testid="pan-mode-btn"
              title="Modo Paneo (mantén Espacio)"
            >
              <Hand className="w-4 h-4" />
            </Button>
            {isDrawingMode && (
              <Button variant="secondary" size="sm" className="h-7 px-2 bg-blue-100 text-blue-700" onClick={exitDrawingMode} data-testid="drawing-mode-btn">
                <Pencil className="w-3.5 h-3.5 mr-1" /><span className="text-xs">Dibujando</span><X className="w-3 h-3 ml-1" />
              </Button>
            )}

            <div className="h-5 w-px bg-stone-200 mx-1" />

            {/* Grid toggle with size */}
            <div className="flex items-center gap-1">
              <Switch checked={showGrid} onCheckedChange={setShowGrid} data-testid="grid-toggle" className="scale-75" />
              <Select value={String(gridSize)} onValueChange={(v) => setGridSize(Number(v))} disabled={!showGrid}>
                <SelectTrigger className="h-7 w-12 text-xs border-0 bg-transparent px-1" data-testid="grid-size-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GRID_SIZES.map(size => (<SelectItem key={size} value={String(size)}>{size}%</SelectItem>))}
                </SelectContent>
              </Select>
            </div>

            <div className="h-5 w-px bg-stone-200 mx-1" />

            {/* Undo/Redo */}
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleUndo} disabled={!canUndo} data-testid="undo-btn" title="Deshacer">
              <Undo2 className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleRedo} disabled={!canRedo} data-testid="redo-btn" title="Rehacer">
              <Redo2 className="w-4 h-4" />
            </Button>

            <div className="h-5 w-px bg-stone-200 mx-1" />

            {/* Cut/Copy/Paste */}
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleCut} disabled={selectedItems.length === 0} data-testid="cut-btn" title="Cortar">
              <Scissors className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleCopy} disabled={selectedItems.length === 0} data-testid="copy-btn" title="Copiar">
              <Copy className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handlePaste} disabled={!clipboard || clipboard.length === 0} data-testid="paste-btn" title="Pegar">
              <ClipboardPaste className="w-4 h-4" />
            </Button>

            <div className="h-5 w-px bg-stone-200 mx-1" />

            {/* Layer controls */}
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleBringToFront} disabled={selectedItems.length === 0} data-testid="bring-to-front-btn" title={t('bringToFront')}>
              <ChevronsUp className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleBringForward} disabled={selectedItems.length === 0} data-testid="bring-forward-btn" title={t('bringForward')}>
              <ArrowUp className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleSendBackward} disabled={selectedItems.length === 0} data-testid="send-backward-btn" title={t('sendBackward')}>
              <ArrowDown className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={handleSendToBack} disabled={selectedItems.length === 0} data-testid="send-to-back-btn" title={t('sendToBack')}>
              <ChevronsDown className="w-4 h-4" />
            </Button>

            <div className="h-5 w-px bg-stone-200 mx-1" />

            {/* Merge */}
            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={handleMergeItems} disabled={selectedItems.length < 2} data-testid="merge-items-btn" title="Fusionar">
              <Combine className="w-4 h-4" />
            </Button>
          </div>

          {/* Right section: Main actions */}
          <div className="flex items-center gap-2">
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
            <input ref={imageInputRef} type="file" accept="image/png,image/gif,image/webp,image/svg+xml" onChange={handleAddImage} className="hidden" />

            <LanguageSelector variant="dark" showLabel={false} />

            <Button variant="outline" size="sm" className="h-8" onClick={() => window.open(`/menu/${menuId}`, '_blank')} data-testid="preview-btn">
              <Eye className="w-4 h-4 mr-1" />
              Preview
            </Button>

            {/* Export dropdown */}
            <div className="relative group">
              <Button variant="outline" size="sm" className="h-8" disabled={isExporting}>
                <Download className="w-4 h-4 mr-1" />
                {isExporting ? "..." : t('export')}
              </Button>
              <div className="absolute right-0 top-full mt-1 bg-white border border-stone-200 rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                <button onClick={() => handleExportImage('png')} className="w-full px-4 py-2 text-sm text-left hover:bg-stone-100">{t('exportPNG')}</button>
                <button onClick={() => handleExportImage('jpeg')} className="w-full px-4 py-2 text-sm text-left hover:bg-stone-100">{t('exportJPG')}</button>
              </div>
            </div>

            <Dialog open={isCanvasSettingsOpen} onOpenChange={setIsCanvasSettingsOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" className="h-8" data-testid="canvas-settings-btn">
                  <Settings className="w-4 h-4" />
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('configureCanvasSize')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>{t('presets')}</Label>
                    <div className="grid grid-cols-2 gap-2">
                      {CANVAS_PRESETS.slice(0, -1).map((preset) => (
                        <Button
                          key={preset.label}
                          variant={tempCanvasConfig.width === preset.width && tempCanvasConfig.height === preset.height ? "default" : "outline"}
                          size="sm"
                          onClick={() => handlePresetSelect(preset)}
                          className="text-xs"
                        >
                          {preset.label}
                        </Button>
                      ))}
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Ancho (px)</Label>
                      <Input
                        type="number"
                        value={tempCanvasConfig.width}
                        onChange={(e) => setTempCanvasConfig(prev => ({ ...prev, width: parseInt(e.target.value) || 0 }))}
                        className="bg-stone-50"
                        data-testid="canvas-width-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Alto (px)</Label>
                      <Input
                        type="number"
                        value={tempCanvasConfig.height}
                        onChange={(e) => setTempCanvasConfig(prev => ({ ...prev, height: parseInt(e.target.value) || 0 }))}
                        className="bg-stone-50"
                        data-testid="canvas-height-input"
                      />
                    </div>
                  </div>
                  
                  <div className="p-3 bg-stone-50 rounded-md text-sm text-stone-600">
                    <p>Aspect Ratio: {(tempCanvasConfig.width / tempCanvasConfig.height).toFixed(2)}</p>
                  </div>

                  <div className="space-y-2 pt-4 border-t border-stone-200">
                    <Label>Intervalo de actualización (segundos)</Label>
                    <p className="text-xs text-stone-500">Frecuencia de refresco en la vista pública</p>
                    <div className="flex gap-2">
                      {REFRESH_OPTIONS.map((interval) => (
                        <Button
                          key={interval}
                          variant={tempRefreshInterval === interval ? "default" : "outline"}
                          size="sm"
                          onClick={() => setTempRefreshInterval(interval)}
                          className="text-xs"
                          data-testid={`refresh-interval-${interval}`}
                        >
                          {interval}s
                        </Button>
                      ))}
                    </div>
                    <Input
                      type="number"
                      min={1}
                      max={300}
                      value={tempRefreshInterval}
                      onChange={(e) => setTempRefreshInterval(Math.max(1, parseInt(e.target.value) || 10))}
                      className="bg-stone-50 w-24"
                      data-testid="refresh-interval-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCanvasSettingsOpen(false)}>
                    {getToastMsg(language, 'cancel')}
                  </Button>
                  <Button 
                    onClick={handleApplyCanvasConfig}
                    className="bg-stone-900 hover:bg-stone-800"
                    data-testid="apply-canvas-size-btn"
                  >
                    {t('apply')}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <Button 
              size="sm"
              onClick={handleSaveMenu}
              disabled={isSaving}
              className="bg-stone-900 hover:bg-stone-800 h-8"
              data-testid="save-btn"
            >
              <Save className="w-4 h-4 mr-1" />
              {isSaving ? "..." : getToastMsg(language, 'save')}
            </Button>
          </div>
        </div>
      </div>

      {/* Main editor area with left sidebar */}
      <div className="flex h-[calc(100vh-49px)]">
        {/* Left Sidebar - Element Tools */}
        <div className="w-16 bg-white border-r border-stone-200 flex flex-col py-2 gap-1">
          {/* Fondo */}
          <button
            onClick={() => fileInputRef.current?.click()}
            className="flex flex-col items-center justify-center gap-1 py-3 mx-1 rounded-lg hover:bg-stone-100 transition-colors group"
            data-testid="sidebar-upload-bg-btn"
          >
            <Upload className="w-5 h-5 text-stone-600 group-hover:text-stone-900" />
            <span className="text-[10px] text-stone-500 group-hover:text-stone-700">Fondo</span>
          </button>

          {/* Imagen */}
          <button
            onClick={() => imageInputRef.current?.click()}
            className="flex flex-col items-center justify-center gap-1 py-3 mx-1 rounded-lg hover:bg-stone-100 transition-colors group"
            data-testid="sidebar-add-image-btn"
          >
            <Image className="w-5 h-5 text-stone-600 group-hover:text-stone-900" />
            <span className="text-[10px] text-stone-500 group-hover:text-stone-700">Imagen</span>
          </button>

          {/* Galería de Medios */}
          <button
            onClick={() => openLibrary("insert", "all")}
            className="flex flex-col items-center justify-center gap-1 py-3 mx-1 rounded-lg hover:bg-stone-100 transition-colors group"
            data-testid="sidebar-library-btn"
            title="Galería de Medios"
          >
            <Layers className="w-5 h-5 text-stone-600 group-hover:text-stone-900" />
            <span className="text-[10px] text-stone-500 group-hover:text-stone-700">Galería</span>
          </button>

          {/* Video/GIF */}
          <Dialog open={isAddVideoOpen} onOpenChange={(open) => { setIsAddVideoOpen(open); if (!open) resetVideoDialog(); }}>
            <DialogTrigger asChild>
              <button
                className="flex flex-col items-center justify-center gap-1 py-3 mx-1 rounded-lg hover:bg-stone-100 transition-colors group"
                data-testid="sidebar-add-video-btn"
              >
                <Video className="w-5 h-5 text-stone-600 group-hover:text-stone-900" />
                <span className="text-[10px] text-stone-500 group-hover:text-stone-700">Video/GIF</span>
              </button>
            </DialogTrigger>
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Film className="w-5 h-5" /> Agregar Video o GIF
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  {/* Source selection */}
                  <div className="space-y-2">
                    <Label>Fuente del video</Label>
                    <div className="grid grid-cols-5 gap-2">
                      {[
                        { value: 'upload', label: 'Subir', icon: Upload },
                        { value: 'gif', label: 'GIF', icon: RefreshCw },
                        { value: 'youtube', label: 'YouTube', icon: Play },
                        { value: 'vimeo', label: 'Vimeo', icon: Film },
                        { value: 'direct_url', label: 'URL', icon: Link },
                      ].map(({ value, label, icon: Icon }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setVideoSource(value)}
                          className={`flex flex-col items-center gap-1 p-3 rounded-lg border transition-colors ${
                            videoSource === value 
                              ? 'border-blue-500 bg-blue-50 text-blue-700' 
                              : 'border-stone-200 hover:bg-stone-50'
                          }`}
                        >
                          <Icon className="w-5 h-5" />
                          <span className="text-xs font-medium">{label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Direct URL section */}
                  {videoSource === 'direct_url' && (
                    <div className="space-y-3 p-3 bg-green-50 rounded-lg border border-green-100">
                      <Label className="text-xs text-green-700 uppercase tracking-wide flex items-center gap-1">
                        <Link className="w-3 h-3" /> Video desde URL (sin almacenamiento)
                      </Label>
                      <div className="space-y-2">
                        <Label className="text-xs">URL del video (MP4, WebM)</Label>
                        <Input
                          placeholder="https://ejemplo.com/video.mp4"
                          value={directVideoUrl}
                          onChange={(e) => setDirectVideoUrl(e.target.value)}
                          className="bg-white"
                        />
                        <p className="text-[10px] text-green-600">
                          El video se reproducirá directamente desde la URL. No se almacena en el sistema.
                        </p>
                      </div>
                      <Button 
                        onClick={handleAddDirectVideoUrl}
                        className="w-full bg-green-600 hover:bg-green-700"
                        disabled={!directVideoUrl}
                      >
                        <Link className="w-4 h-4 mr-2" />
                        Agregar Video URL
                      </Button>
                    </div>
                  )}

                  {/* Upload section */}
                  {(videoSource === 'upload' || videoSource === 'gif') && (
                    <div className="space-y-3">
                      <div className="border-2 border-dashed border-stone-300 rounded-lg p-6 text-center hover:border-blue-400 transition-colors">
                        <input
                          type="file"
                          id="video-upload"
                          accept={videoSource === 'gif' ? 'image/gif' : 'video/mp4,video/webm,video/quicktime'}
                          onChange={handleAddVideoFile}
                          className="hidden"
                        />
                        <label htmlFor="video-upload" className="cursor-pointer">
                          {isUploadingVideo ? (
                            <div className="flex flex-col items-center gap-2">
                              <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full" />
                              <span className="text-sm text-stone-500">Subiendo...</span>
                            </div>
                          ) : (
                            <>
                              <Upload className="w-10 h-10 mx-auto text-stone-400 mb-2" />
                              <p className="text-sm text-stone-600">
                                {videoSource === 'gif' 
                                  ? 'Haz clic para subir un GIF' 
                                  : 'Haz clic para subir un video'}
                              </p>
                              <p className="text-xs text-stone-400 mt-1">
                                {videoSource === 'gif' ? 'GIF (máx. 25MB)' : 'MP4, WebM, MOV (máx. 25MB)'}
                              </p>
                            </>
                          )}
                        </label>
                      </div>
                    </div>
                  )}

                  {/* URL input for YouTube/Vimeo */}
                  {(videoSource === 'youtube' || videoSource === 'vimeo') && (
                    <div className="space-y-3">
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Link className="w-4 h-4" /> URL del video
                        </Label>
                        <Input
                          value={videoUrl}
                          onChange={(e) => setVideoUrl(e.target.value)}
                          placeholder={videoSource === 'youtube' 
                            ? 'https://www.youtube.com/watch?v=...' 
                            : 'https://vimeo.com/...'}
                          className="bg-stone-50"
                        />
                      </div>
                    </div>
                  )}

                  {/* Video options */}
                  <div className="space-y-3 pt-2 border-t">
                    <Label className="text-stone-500 text-xs uppercase tracking-wide">Opciones de reproducción</Label>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {videoMuted ? <VolumeX className="w-4 h-4 text-stone-500" /> : <Volume2 className="w-4 h-4 text-green-600" />}
                        <span className="text-sm">Silenciado</span>
                      </div>
                      <Switch
                        checked={videoMuted}
                        onCheckedChange={setVideoMuted}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <RefreshCw className="w-4 h-4 text-stone-500" />
                        <span className="text-sm">Repetir (loop)</span>
                      </div>
                      <Switch
                        checked={videoLoop}
                        onCheckedChange={setVideoLoop}
                      />
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Play className="w-4 h-4 text-stone-500" />
                        <span className="text-sm">Reproducción automática</span>
                      </div>
                      <Switch
                        checked={videoAutoplay}
                        onCheckedChange={setVideoAutoplay}
                      />
                    </div>
                  </div>

                  {videoSource === 'upload' && (
                    <p className="text-xs text-amber-600 bg-amber-50 p-2 rounded">
                      💡 Tip: Activa "Silenciado" antes de subir para reducir el tamaño del archivo.
                    </p>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsAddVideoOpen(false)}>
                    {getToastMsg(language, 'cancel')}
                  </Button>
                  {(videoSource === 'youtube' || videoSource === 'vimeo') && (
                    <Button onClick={handleAddExternalVideo} className="bg-blue-600 hover:bg-blue-700">
                      {t('addVideo')}
                    </Button>
                  )}
                </DialogFooter>
              </DialogContent>
          </Dialog>

          {/* Texto */}
          <Dialog open={isAddItemOpen} onOpenChange={setIsAddItemOpen}>
            <DialogTrigger asChild>
              <button
                className="flex flex-col items-center justify-center gap-1 py-3 mx-1 rounded-lg hover:bg-stone-100 transition-colors group"
                data-testid="sidebar-add-text-btn"
              >
                <Type className="w-5 h-5 text-stone-600 group-hover:text-stone-900" />
                <span className="text-[10px] text-stone-500 group-hover:text-stone-700">Texto</span>
              </button>
            </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>{t('addText')}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>{t('textType')}</Label>
                    <Select value={newItemType} onValueChange={setNewItemType}>
                      <SelectTrigger className="bg-stone-50" data-testid="item-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="name">
                          <div className="flex items-center gap-2">
                            <Type className="w-4 h-4" />
                            {t('textName')}
                          </div>
                        </SelectItem>
                        <SelectItem value="price">
                          <div className="flex items-center gap-2">
                            <DollarSign className="w-4 h-4" />
                            {t('textPrice')}
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{newItemType === "name" ? getToastMsg(language, 'productName') : getToastMsg(language, 'price')}</Label>
                    <Input
                      placeholder={newItemType === "name" ? "Ej: Pizza Margherita" : "Ej: $15.00"}
                      value={newItemText}
                      onChange={(e) => setNewItemText(e.target.value)}
                      className="bg-stone-50"
                      data-testid="new-item-text-input"
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsAddItemOpen(false)}>
                    {getToastMsg(language, 'cancel')}
                  </Button>
                  <Button 
                    onClick={handleAddItem}
                    className="bg-stone-900 hover:bg-stone-800"
                    data-testid="confirm-add-item-btn"
                  >
                    {t('add')}
                  </Button>
                </DialogFooter>
              </DialogContent>
          </Dialog>

          {/* Formas */}
          <Dialog open={isAddShapeOpen} onOpenChange={setIsAddShapeOpen}>
            <DialogTrigger asChild>
              <button
                className="flex flex-col items-center justify-center gap-1 py-3 mx-1 rounded-lg hover:bg-stone-100 transition-colors group"
                data-testid="sidebar-add-shape-btn"
              >
                <Square className="w-5 h-5 text-stone-600 group-hover:text-stone-900" />
                <span className="text-[10px] text-stone-500 group-hover:text-stone-700">Formas</span>
              </button>
            </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Square className="w-5 h-5" /> Agregar Forma
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Tipo de Forma</Label>
                    <Select value={shapeType} onValueChange={setShapeType}>
                      <SelectTrigger className="bg-stone-50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SHAPE_TYPES.map(shape => (
                          <SelectItem key={shape.value} value={shape.value}>
                            <div className="flex items-center gap-2">
                              <span>{shape.icon}</span> {shape.label}
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs">Color de relleno</Label>
                      <input
                        type="color"
                        value={shapeFill}
                        onChange={(e) => setShapeFill(e.target.value)}
                        className="w-full h-10 rounded border cursor-pointer"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Color de borde</Label>
                      <input
                        type="color"
                        value={shapeStroke}
                        onChange={(e) => setShapeStroke(e.target.value)}
                        className="w-full h-10 rounded border cursor-pointer"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label className="text-xs">Grosor de borde</Label>
                      <Select value={String(shapeStrokeWidth)} onValueChange={(v) => setShapeStrokeWidth(parseInt(v))}>
                        <SelectTrigger className="bg-stone-50">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {[0, 1, 2, 3, 4, 5, 8, 10].map(w => (
                            <SelectItem key={w} value={String(w)}>
                              {w === 0 ? 'Sin borde' : `${w}px`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    {shapeType === 'rectangle' && (
                      <div className="space-y-2">
                        <Label className="text-xs">Radio de esquinas</Label>
                        <Select value={String(shapeBorderRadius)} onValueChange={(v) => setShapeBorderRadius(parseInt(v))}>
                          <SelectTrigger className="bg-stone-50">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[0, 5, 10, 15, 20, 30, 50, 100].map(r => (
                              <SelectItem key={r} value={String(r)}>
                                {r === 0 ? 'Sin radio' : `${r}px`}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                  </div>

                  {/* Shape Preview */}
                  <div className="space-y-2">
                    <Label className="text-xs">Vista previa</Label>
                    <div className="bg-stone-800 rounded-lg p-4 flex items-center justify-center h-24">
                      {shapeType === 'rectangle' && (
                        <div style={{
                          width: '60px',
                          height: '40px',
                          backgroundColor: shapeFill,
                          border: shapeStrokeWidth > 0 ? `${shapeStrokeWidth}px solid ${shapeStroke}` : 'none',
                          borderRadius: `${shapeBorderRadius}px`,
                        }} />
                      )}
                      {shapeType === 'circle' && (
                        <div style={{
                          width: '50px',
                          height: '50px',
                          backgroundColor: shapeFill,
                          border: shapeStrokeWidth > 0 ? `${shapeStrokeWidth}px solid ${shapeStroke}` : 'none',
                          borderRadius: '50%',
                        }} />
                      )}
                      {shapeType === 'triangle' && (
                        <div style={{
                          width: 0,
                          height: 0,
                          borderLeft: '30px solid transparent',
                          borderRight: '30px solid transparent',
                          borderBottom: `50px solid ${shapeFill}`,
                        }} />
                      )}
                      {shapeType === 'star' && (
                        <span style={{ fontSize: '48px', color: shapeFill }}>★</span>
                      )}
                      {shapeType === 'hexagon' && (
                        <span style={{ fontSize: '48px', color: shapeFill }}>⬡</span>
                      )}
                      {shapeType === 'line' && (
                        <div style={{
                          width: '80px',
                          height: `${Math.max(shapeStrokeWidth, 3)}px`,
                          backgroundColor: shapeFill,
                        }} />
                      )}
                      {shapeType === 'arrow' && (
                        <ArrowRight style={{ width: '60px', height: '40px', color: shapeFill }} />
                      )}
                    </div>
                  </div>
                </div>
                <DialogFooter className="flex-col sm:flex-row gap-2">
                  <Button variant="outline" onClick={() => { setIsAddShapeOpen(false); resetShapeDialog(); }}>
                    {getToastMsg(language, 'cancel')}
                  </Button>
                  <Button 
                    onClick={handleAddShape}
                    variant="outline"
                    className="border-stone-300"
                    data-testid="confirm-add-shape-btn"
                  >
                    {t('quickAdd')}
                  </Button>
                  <Button 
                    onClick={handleAddShapeDrawMode}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="draw-shape-btn"
                  >
                    <MousePointer2 className="w-4 h-4 mr-2" />
                    {t('drawOnCanvas')}
                  </Button>
                </DialogFooter>
              </DialogContent>
          </Dialog>

          {/* Widgets */}
          <Dialog open={isAddWidgetOpen} onOpenChange={setIsAddWidgetOpen}>
            <DialogTrigger asChild>
              <button
                className="flex flex-col items-center justify-center gap-1 py-3 mx-1 rounded-lg hover:bg-stone-100 transition-colors group"
                data-testid="sidebar-add-widget-btn"
              >
                <Sparkles className="w-5 h-5 text-stone-600 group-hover:text-stone-900" />
                <span className="text-[10px] text-stone-500 group-hover:text-stone-700">Widgets</span>
              </button>
            </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5" /> Agregar Widget
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Tipo de Widget</Label>
                    <Select value={widgetType} onValueChange={setWidgetType}>
                      <SelectTrigger className="bg-stone-50">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="clock">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4" /> Reloj Digital
                          </div>
                        </SelectItem>
                        <SelectItem value="weather">
                          <div className="flex items-center gap-2">
                            <Cloud className="w-4 h-4" /> Clima
                          </div>
                        </SelectItem>
                        <SelectItem value="ticker">
                          <div className="flex items-center gap-2">
                            <MessageSquare className="w-4 h-4" /> Ticker de Noticias
                          </div>
                        </SelectItem>
                        <SelectItem value="rss">
                          <div className="flex items-center gap-2">
                            <Rss className="w-4 h-4" /> Feed RSS
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Clock Options */}
                  {widgetType === 'clock' && (
                    <div className="space-y-3 p-3 bg-blue-50 rounded-lg border border-blue-100">
                      <Label className="text-xs text-blue-700 uppercase tracking-wide flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Configuración del Reloj
                      </Label>
                      <div className="space-y-2">
                        <Label className="text-xs">Formato</Label>
                        <Select value={clockFormat} onValueChange={setClockFormat}>
                          <SelectTrigger className="bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="12h">12 horas (AM/PM)</SelectItem>
                            <SelectItem value="24h">24 horas</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={clockShowDate}
                          onChange={(e) => setClockShowDate(e.target.checked)}
                          className="rounded"
                        />
                        <Label className="text-xs">Mostrar fecha</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={clockShowSeconds}
                          onChange={(e) => setClockShowSeconds(e.target.checked)}
                          className="rounded"
                        />
                        <Label className="text-xs">Mostrar segundos</Label>
                      </div>
                    </div>
                  )}

                  {/* Weather Options */}
                  {widgetType === 'weather' && (
                    <div className="space-y-3 p-3 bg-sky-50 rounded-lg border border-sky-100">
                      <Label className="text-xs text-sky-700 uppercase tracking-wide flex items-center gap-1">
                        <Cloud className="w-3 h-3" /> Configuración del Clima
                      </Label>
                      <div className="space-y-2">
                        <Label className="text-xs">Ciudad</Label>
                        <Input
                          placeholder="Ej: Madrid, ES"
                          value={weatherCity}
                          onChange={(e) => setWeatherCity(e.target.value)}
                          className="bg-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">API Key de OpenWeatherMap</Label>
                        <Input
                          type="password"
                          placeholder="Tu API key..."
                          value={weatherApiKey}
                          onChange={(e) => setWeatherApiKey(e.target.value)}
                          className="bg-white"
                        />
                        <p className="text-[10px] text-sky-500">
                          Obtén tu key gratis en <a href="https://openweathermap.org/api" target="_blank" rel="noreferrer" className="underline">openweathermap.org</a>
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Unidades</Label>
                        <Select value={weatherUnits} onValueChange={setWeatherUnits}>
                          <SelectTrigger className="bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="metric">Celsius (°C)</SelectItem>
                            <SelectItem value="imperial">Fahrenheit (°F)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}

                  {/* Ticker Options */}
                  {widgetType === 'ticker' && (
                    <div className="space-y-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
                      <Label className="text-xs text-amber-700 uppercase tracking-wide flex items-center gap-1">
                        <MessageSquare className="w-3 h-3" /> Configuración del Ticker
                      </Label>
                      <div className="space-y-2">
                        <Label className="text-xs">Texto del Ticker</Label>
                        <Input
                          placeholder="Ej: ¡Bienvenidos! Hoy tenemos 20% de descuento..."
                          value={tickerText}
                          onChange={(e) => setTickerText(e.target.value)}
                          className="bg-white"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Velocidad (segundos para completar)</Label>
                        <Select value={String(tickerSpeed)} onValueChange={(v) => setTickerSpeed(parseInt(v))}>
                          <SelectTrigger className="bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="15">Rápido (15s)</SelectItem>
                            <SelectItem value="30">Normal (30s)</SelectItem>
                            <SelectItem value="45">Lento (45s)</SelectItem>
                            <SelectItem value="60">Muy lento (60s)</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Color de fondo</Label>
                          <input
                            type="color"
                            value={tickerBgColor}
                            onChange={(e) => setTickerBgColor(e.target.value)}
                            className="w-full h-8 rounded border cursor-pointer"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Color de texto</Label>
                          <input
                            type="color"
                            value={tickerTextColor}
                            onChange={(e) => setTickerTextColor(e.target.value)}
                            className="w-full h-8 rounded border cursor-pointer"
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  {/* RSS Options */}
                  {widgetType === 'rss' && (
                    <div className="space-y-3 p-3 bg-orange-50 rounded-lg border border-orange-100">
                      <Label className="text-xs text-orange-700 uppercase tracking-wide flex items-center gap-1">
                        <Rss className="w-3 h-3" /> Configuración del Feed RSS
                      </Label>
                      <div className="space-y-2">
                        <Label className="text-xs">Feed Predefinido</Label>
                        <Select value={rssPreset} onValueChange={(v) => { setRssPreset(v); if (v) setRssUrl(""); }}>
                          <SelectTrigger className="bg-white">
                            <SelectValue placeholder="Selecciona un feed..." />
                          </SelectTrigger>
                          <SelectContent>
                            {RSS_PRESETS.map(preset => (
                              <SelectItem key={preset.key || 'custom'} value={preset.key}>
                                {preset.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      {(!rssPreset || rssPreset === 'custom') && (
                        <div className="space-y-2">
                          <Label className="text-xs">URL del Feed RSS</Label>
                          <Input
                            placeholder="https://example.com/rss.xml"
                            value={rssUrl}
                            onChange={(e) => setRssUrl(e.target.value)}
                            className="bg-white"
                          />
                        </div>
                      )}
                      <div className="space-y-2">
                        <Label className="text-xs">Máximo de noticias</Label>
                        <Select value={String(rssMaxItems)} onValueChange={(v) => setRssMaxItems(parseInt(v))}>
                          <SelectTrigger className="bg-white">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[3, 5, 10, 15].map(n => (
                              <SelectItem key={n} value={String(n)}>{n} noticias</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setIsAddWidgetOpen(false); resetWidgetDialog(); }}>
                    {getToastMsg(language, 'cancel')}
                  </Button>
                  <Button 
                    onClick={handleAddWidget}
                    className="bg-stone-900 hover:bg-stone-800"
                    data-testid="confirm-add-widget-btn"
                  >
                    {t('addWidget')}
                  </Button>
                </DialogFooter>
              </DialogContent>
          </Dialog>
        </div>

        {/* Canvas Area */}
        <div 
          className="flex-1 p-6 overflow-auto flex items-center justify-center bg-stone-200"
          onMouseMove={handleCanvasMouseMove}
          onMouseUp={handleCanvasMouseUp}
          onMouseLeave={handleCanvasMouseLeave}
        >
          <div 
            className="relative" 
            style={{ 
              padding: showRulers ? '20px 0 0 28px' : 0,
              transform: `translate(${panOffset.x}px, ${panOffset.y}px)`,
              cursor: isPanMode ? (isPanning ? 'grabbing' : 'grab') : 'default'
            }}
          >
            {renderRulers()}
            <div 
              ref={canvasRef}
              className={`relative bg-stone-800 shadow-2xl overflow-hidden ${isDrawingMode ? 'cursor-crosshair' : ''} ${isPanMode || isSpacePressed ? 'cursor-grab' : ''}`}
              style={{
                width: canvasSize.width,
                height: canvasSize.height,
                backgroundImage: menu?.background_image ? `url(${menu.background_image})` : 'none',
                backgroundSize: '100% 100%',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
              onClick={() => !isPanMode && !isDrawingMode && setSelectedItems([])}
              onMouseDown={(e) => {
                if (isDrawingMode && !isPanMode) {
                  handleCanvasDrawStart(e);
                } else {
                  handleCanvasMouseDown(e);
                }
              }}
              onMouseMove={isDrawing ? handleCanvasDrawMove : undefined}
              onMouseUp={isDrawing ? handleCanvasDrawEnd : undefined}
              onMouseLeave={isDrawing ? handleCanvasDrawEnd : undefined}
              data-testid="editor-canvas"
            >
              {!menu?.background_image && (
                <div className="absolute inset-0 flex items-center justify-center text-stone-500">
                  <div className="text-center">
                    <Upload className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Sube una imagen de fondo</p>
                    <p className="text-sm mt-2 opacity-75">{canvasConfig.width} x {canvasConfig.height}px</p>
                  </div>
                </div>
              )}

              {renderGrid()}

              {/* Drawing preview */}
              {isDrawing && drawingStart && drawingCurrent && drawingConfig && (
                <div
                  className="absolute border-2 border-dashed border-blue-500 bg-blue-500/20 pointer-events-none"
                  style={{
                    left: `${Math.min(drawingStart.x, drawingCurrent.x)}%`,
                    top: `${Math.min(drawingStart.y, drawingCurrent.y)}%`,
                    width: `${Math.abs(drawingCurrent.x - drawingStart.x)}%`,
                    height: `${Math.abs(drawingCurrent.y - drawingStart.y)}%`,
                    backgroundColor: drawingConfig.shape_fill ? `${drawingConfig.shape_fill}40` : 'rgba(59, 130, 246, 0.2)',
                    borderRadius: drawingConfig.shape_type === 'circle' ? '50%' : `${drawingConfig.shape_border_radius || 0}px`,
                  }}
                >
                  <div className="absolute -top-6 left-0 bg-blue-600 text-white text-xs px-2 py-0.5 rounded whitespace-nowrap">
                    {Math.abs(drawingCurrent.x - drawingStart.x).toFixed(0)}% x {Math.abs(drawingCurrent.y - drawingStart.y).toFixed(0)}%
                  </div>
                </div>
              )}

              {menu?.items?.map((item) => (
                <DraggableMenuItem
                  key={item.id}
                  item={item}
                  isSelected={selectedItems.some(sel => sel.id === item.id)}
                  onDragStop={handleDragStop}
                  onClick={(clickedItem, event) => {
                    // Handle multi-selection with Shift key
                    if (event?.shiftKey) {
                      setSelectedItems(prev => {
                        const isAlreadySelected = prev.some(sel => sel.id === clickedItem.id);
                        if (isAlreadySelected) {
                          // Remove from selection
                          return prev.filter(sel => sel.id !== clickedItem.id);
                        } else {
                          // Add to selection
                          return [...prev, clickedItem];
                        }
                      });
                    } else {
                      // Single selection (replace)
                      setSelectedItems([clickedItem]);
                    }
                  }}
                  onResize={handleResize}
                  canvasSize={canvasSize}
                  canvasConfig={canvasConfig}
                  isPanMode={isPanMode}
                  selectedCount={selectedItems.length}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Properties panel */}
        <div className="w-80 bg-white border-l border-stone-200 overflow-y-auto">
          {selectedItems.length > 1 ? (
            /* Multi-selection mode */
            <div className="p-4 space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-stone-900">{selectedItems.length} items seleccionados</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleDeleteSelectedItems}
                  className="text-red-600 hover:bg-red-50"
                  data-testid="delete-selected-items-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-4">
                <div className="p-4 bg-blue-50 rounded-lg text-sm text-blue-700">
                  <p className="font-medium mb-2">Selección múltiple activa</p>
                  <ul className="text-xs space-y-1 text-blue-600">
                    <li>• <strong>Ctrl+C</strong> para copiar todos</li>
                    <li>• <strong>Ctrl+X</strong> para cortar todos</li>
                    <li>• <strong>Delete</strong> para eliminar todos</li>
                    <li>• <strong>Shift+Click</strong> para agregar/quitar</li>
                    <li>• <strong>Ctrl+A</strong> seleccionar todos</li>
                    <li>• <strong>Escape</strong> para deseleccionar</li>
                  </ul>
                </div>

                {/* List of selected items */}
                <div className="space-y-2">
                  <Label>Items seleccionados:</Label>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {selectedItems.map((item, idx) => (
                      <div key={item.id} className="flex items-center justify-between px-2 py-1 bg-stone-50 rounded text-xs">
                        <span className="truncate flex-1">
                          {item.type === 'image' ? `🖼️ ${item.label || 'Imagen'}` : `📝 ${item.text?.substring(0, 20) || item.label || 'Texto'}${(item.text?.length || 0) > 20 ? '...' : ''}`}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 w-5 p-0 text-stone-400 hover:text-red-500"
                          onClick={() => setSelectedItems(prev => prev.filter(sel => sel.id !== item.id))}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Common properties that can be changed for all selected items */}
                <div className="pt-4 border-t border-stone-200 space-y-4">
                  <Label className="text-stone-500 text-xs">Aplicar a todos los seleccionados:</Label>
                  
                  <div className="space-y-2">
                    <Label>Color</Label>
                    <div className="flex gap-2 flex-wrap">
                      {["#ffffff", "#000000", "#ef4444", "#f97316", "#eab308", "#22c55e", "#3b82f6", "#8b5cf6"].map(color => (
                        <button
                          key={color}
                          className="w-6 h-6 rounded border border-stone-300 hover:scale-110 transition-transform"
                          style={{ backgroundColor: color }}
                          onClick={() => handleUpdateItem("color", color)}
                        />
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Opacidad</Label>
                    <Slider
                      value={[100]}
                      onValueChange={([value]) => handleUpdateItem("opacity", value / 100)}
                      min={10}
                      max={100}
                      step={5}
                      className="py-2"
                    />
                  </div>
                </div>
              </div>
            </div>
          ) : selectedItem ? (
            <div className="p-4 space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-stone-900">{t('properties')}</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteItem(selectedItem.id)}
                  className="text-red-600 hover:bg-red-50"
                  data-testid="delete-selected-item-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-4">
                <div className="px-2 py-1 bg-stone-100 rounded text-xs text-stone-500 inline-block">
                  {selectedItem.type === "name" ? getToastMsg(language, 'text') : selectedItem.type === "price" ? getToastMsg(language, 'price') : selectedItem.type === "image" ? getToastMsg(language, 'image') : selectedItem.type === "video" ? getToastMsg(language, 'video') : selectedItem.type === "gif" ? "GIF" : getToastMsg(language, 'element')} | {getToastMsg(language, 'pos')}: {selectedItem.x.toFixed(1)}%, {selectedItem.y.toFixed(1)}%
                </div>

                {/* Video/GIF Properties */}
                {(selectedItem.type === 'video' || selectedItem.type === 'gif') && (
                  <>
                    <div className="space-y-2">
                      <Label>{t('label')}</Label>
                      <Input
                        value={selectedItem.label || ""}
                        onChange={(e) => handleUpdateItem("label", e.target.value)}
                        placeholder={getToastMsg(language, 'videoName')}
                        className="bg-stone-50"
                      />
                    </div>

                    <div className="space-y-3 p-3 bg-stone-50 rounded-lg">
                      <Label className="text-xs text-stone-500 uppercase tracking-wide">Opciones de Video</Label>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          {selectedItem.video_muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4 text-green-600" />}
                          <span className="text-sm">Silenciado</span>
                        </div>
                        <Switch
                          checked={selectedItem.video_muted !== false}
                          onCheckedChange={(checked) => handleUpdateItem("video_muted", checked)}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <RefreshCw className="w-4 h-4" />
                          <span className="text-sm">Repetir</span>
                        </div>
                        <Switch
                          checked={selectedItem.video_loop !== false}
                          onCheckedChange={(checked) => handleUpdateItem("video_loop", checked)}
                        />
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Play className="w-4 h-4" />
                          <span className="text-sm">Autoplay</span>
                        </div>
                        <Switch
                          checked={selectedItem.video_autoplay !== false}
                          onCheckedChange={(checked) => handleUpdateItem("video_autoplay", checked)}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Ancho (%)</Label>
                        <Input
                          type="number"
                          value={selectedItem.width?.toFixed(1) || 20}
                          onChange={(e) => handleUpdateItem("width", parseFloat(e.target.value) || 20)}
                          className="bg-stone-50 h-8"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Alto (%)</Label>
                        <Input
                          type="number"
                          value={selectedItem.height?.toFixed(1) || 15}
                          onChange={(e) => handleUpdateItem("height", parseFloat(e.target.value) || 15)}
                          className="bg-stone-50 h-8"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Opacidad</Label>
                      <Slider
                        value={[(selectedItem.opacity || 1) * 100]}
                        onValueChange={([value]) => handleUpdateItem("opacity", value / 100)}
                        min={10}
                        max={100}
                        step={5}
                        className="py-2"
                      />
                    </div>

                    {/* Animation Controls for Video/GIF */}
                    <div className="space-y-3 p-3 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-100">
                      <Label className="text-xs text-purple-700 uppercase tracking-wide flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> Animaciones
                      </Label>
                      
                      <div className="space-y-2">
                        <Label className="text-xs text-stone-600">Animación continua</Label>
                        <Select
                          value={selectedItem.animation || "none"}
                          onValueChange={(value) => handleUpdateItem("animation", value)}
                        >
                          <SelectTrigger className="bg-white" data-testid="video-animation-select">
                            <SelectValue placeholder="Seleccionar animación" />
                          </SelectTrigger>
                          <SelectContent>
                            {ANIMATIONS.map(anim => (
                              <SelectItem key={anim.value} value={anim.value}>
                                <span className="flex items-center gap-2">
                                  <span>{anim.icon}</span>
                                  <span>{anim.label}</span>
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs text-stone-600">Animación de entrada</Label>
                        <Select
                          value={selectedItem.entry_animation || "none"}
                          onValueChange={(value) => handleUpdateItem("entry_animation", value)}
                        >
                          <SelectTrigger className="bg-white" data-testid="video-entry-animation-select">
                            <SelectValue placeholder="Seleccionar entrada" />
                          </SelectTrigger>
                          <SelectContent>
                            {ENTRY_ANIMATIONS.map(anim => (
                              <SelectItem key={anim.value} value={anim.value}>
                                <span className="flex items-center gap-2">
                                  <span>{anim.icon}</span>
                                  <span>{anim.label}</span>
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {(selectedItem.animation && selectedItem.animation !== 'none') && (
                        <div className="space-y-2">
                          <Label className="text-xs text-stone-600">Retraso de animación</Label>
                          <Select
                            value={String(selectedItem.animation_delay || 0)}
                            onValueChange={(value) => handleUpdateItem("animation_delay", parseFloat(value))}
                          >
                            <SelectTrigger className="bg-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[0, 0.2, 0.4, 0.6, 0.8, 1, 1.5, 2, 3].map(delay => (
                                <SelectItem key={delay} value={String(delay)}>
                                  {delay === 0 ? 'Sin retraso' : `${delay}s`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Animation Preview */}
                      {(selectedItem.animation && selectedItem.animation !== 'none') && (
                        <div className="space-y-2">
                          <Label className="text-xs text-stone-600">Vista previa</Label>
                          <div className="bg-stone-800 rounded-lg p-4 flex items-center justify-center h-20 overflow-hidden">
                            <div 
                              className={`w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-400 rounded-lg anim-${selectedItem.animation}`}
                              style={{ animationDelay: `${selectedItem.animation_delay || 0}s` }}
                            />
                          </div>
                        </div>
                      )}

                      <p className="text-[10px] text-purple-500/70 italic">
                        Las animaciones son visibles en la vista pública
                      </p>
                    </div>
                  </>
                )}

                {/* Shape Properties */}
                {selectedItem.type === 'shape' && (
                  <>
                    <div className="space-y-2">
                      <Label>{t('label')}</Label>
                      <Input
                        value={selectedItem.label || ""}
                        onChange={(e) => handleUpdateItem("label", e.target.value)}
                        placeholder={getToastMsg(language, 'shapeName')}
                        className="bg-stone-50"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Ancho (%)</Label>
                        <Input
                          type="number"
                          value={selectedItem.width?.toFixed(1) || 15}
                          onChange={(e) => handleUpdateItem("width", parseFloat(e.target.value) || 15)}
                          className="bg-stone-50 h-8"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Alto (%)</Label>
                        <Input
                          type="number"
                          value={selectedItem.height?.toFixed(1) || 15}
                          onChange={(e) => handleUpdateItem("height", parseFloat(e.target.value) || 15)}
                          className="bg-stone-50 h-8"
                        />
                      </div>
                    </div>

                    <div className="space-y-3 p-3 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg border border-blue-100">
                      <Label className="text-xs text-blue-700 uppercase tracking-wide flex items-center gap-1">
                        <Square className="w-3 h-3" /> Colores
                      </Label>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Relleno</Label>
                          <input
                            type="color"
                            value={selectedItem.shape_fill || "#3b82f6"}
                            onChange={(e) => handleUpdateItem("shape_fill", e.target.value)}
                            className="w-full h-8 rounded border cursor-pointer"
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Borde</Label>
                          <input
                            type="color"
                            value={selectedItem.shape_stroke || "#1e40af"}
                            onChange={(e) => handleUpdateItem("shape_stroke", e.target.value)}
                            className="w-full h-8 rounded border cursor-pointer"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Grosor de borde</Label>
                        <Slider
                          value={[selectedItem.shape_stroke_width || 0]}
                          onValueChange={([value]) => handleUpdateItem("shape_stroke_width", value)}
                          min={0}
                          max={20}
                          step={1}
                          className="py-2"
                        />
                        <span className="text-xs text-stone-500">{selectedItem.shape_stroke_width || 0}px</span>
                      </div>
                      {selectedItem.shape_type === 'rectangle' && (
                        <div className="space-y-2">
                          <Label className="text-xs">Radio de esquinas</Label>
                          <Slider
                            value={[selectedItem.shape_border_radius || 0]}
                            onValueChange={([value]) => handleUpdateItem("shape_border_radius", value)}
                            min={0}
                            max={100}
                            step={5}
                            className="py-2"
                          />
                          <span className="text-xs text-stone-500">{selectedItem.shape_border_radius || 0}px</span>
                        </div>
                      )}
                    </div>

                    <div className="space-y-3 p-3 bg-gradient-to-br from-orange-50 to-yellow-50 rounded-lg border border-orange-100">
                      <Label className="text-xs text-orange-700 uppercase tracking-wide flex items-center gap-1">
                        <RotateCw className="w-3 h-3" /> Transformaciones
                      </Label>
                      <div className="space-y-2">
                        <Label className="text-xs">Rotación</Label>
                        <Slider
                          value={[selectedItem.shape_rotation || 0]}
                          onValueChange={([value]) => handleUpdateItem("shape_rotation", value)}
                          min={0}
                          max={360}
                          step={5}
                          className="py-2"
                        />
                        <span className="text-xs text-stone-500">{selectedItem.shape_rotation || 0}°</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Sesgo X</Label>
                          <Slider
                            value={[selectedItem.shape_skew_x || 0]}
                            onValueChange={([value]) => handleUpdateItem("shape_skew_x", value)}
                            min={-45}
                            max={45}
                            step={5}
                            className="py-2"
                          />
                          <span className="text-xs text-stone-500">{selectedItem.shape_skew_x || 0}°</span>
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Sesgo Y</Label>
                          <Slider
                            value={[selectedItem.shape_skew_y || 0]}
                            onValueChange={([value]) => handleUpdateItem("shape_skew_y", value)}
                            min={-45}
                            max={45}
                            step={5}
                            className="py-2"
                          />
                          <span className="text-xs text-stone-500">{selectedItem.shape_skew_y || 0}°</span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Opacidad</Label>
                      <Slider
                        value={[(selectedItem.opacity || 1) * 100]}
                        onValueChange={([value]) => handleUpdateItem("opacity", value / 100)}
                        min={10}
                        max={100}
                        step={5}
                        className="py-2"
                      />
                    </div>
                  </>
                )}

                {selectedItem.type === 'image' && (
                  <>
                    <div className="space-y-2">
                      <Label>{t('label')}</Label>
                      <Input
                        value={selectedItem.label || ""}
                        onChange={(e) => handleUpdateItem("label", e.target.value)}
                        placeholder={getToastMsg(language, 'imageName')}
                        className="bg-stone-50"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div className="space-y-1">
                        <Label className="text-xs">Ancho (%)</Label>
                        <Input
                          type="number"
                          value={selectedItem.width?.toFixed(1) || 20}
                          onChange={(e) => handleUpdateItem("width", parseFloat(e.target.value) || 20)}
                          className="bg-stone-50 h-8"
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Alto (%)</Label>
                        <Input
                          type="number"
                          value={selectedItem.height?.toFixed(1) || 15}
                          onChange={(e) => handleUpdateItem("height", parseFloat(e.target.value) || 15)}
                          className="bg-stone-50 h-8"
                        />
                      </div>
                    </div>

                    {/* Animation Controls */}
                    <div className="space-y-3 p-3 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-100">
                      <Label className="text-xs text-purple-700 uppercase tracking-wide flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> Animaciones
                      </Label>
                      
                      <div className="space-y-2">
                        <Label className="text-xs text-stone-600">Animación continua</Label>
                        <Select
                          value={selectedItem.animation || "none"}
                          onValueChange={(value) => handleUpdateItem("animation", value)}
                        >
                          <SelectTrigger className="bg-white" data-testid="animation-select">
                            <SelectValue placeholder="Seleccionar animación" />
                          </SelectTrigger>
                          <SelectContent>
                            {ANIMATIONS.map(anim => (
                              <SelectItem key={anim.value} value={anim.value}>
                                <span className="flex items-center gap-2">
                                  <span>{anim.icon}</span>
                                  <span>{anim.label}</span>
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs text-stone-600">Animación de entrada</Label>
                        <Select
                          value={selectedItem.entry_animation || "none"}
                          onValueChange={(value) => handleUpdateItem("entry_animation", value)}
                        >
                          <SelectTrigger className="bg-white" data-testid="entry-animation-select">
                            <SelectValue placeholder="Seleccionar entrada" />
                          </SelectTrigger>
                          <SelectContent>
                            {ENTRY_ANIMATIONS.map(anim => (
                              <SelectItem key={anim.value} value={anim.value}>
                                <span className="flex items-center gap-2">
                                  <span>{anim.icon}</span>
                                  <span>{anim.label}</span>
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {(selectedItem.animation && selectedItem.animation !== 'none') && (
                        <div className="space-y-2">
                          <Label className="text-xs text-stone-600">Retraso de animación</Label>
                          <Select
                            value={String(selectedItem.animation_delay || 0)}
                            onValueChange={(value) => handleUpdateItem("animation_delay", parseFloat(value))}
                          >
                            <SelectTrigger className="bg-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[0, 0.2, 0.4, 0.6, 0.8, 1, 1.5, 2, 3].map(delay => (
                                <SelectItem key={delay} value={String(delay)}>
                                  {delay === 0 ? 'Sin retraso' : `${delay}s`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Animation Preview */}
                      {(selectedItem.animation && selectedItem.animation !== 'none') && (
                        <div className="space-y-2">
                          <Label className="text-xs text-stone-600">Vista previa</Label>
                          <div className="bg-stone-800 rounded-lg p-4 flex items-center justify-center h-20 overflow-hidden">
                            <div 
                              className={`w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-400 rounded-lg anim-${selectedItem.animation}`}
                              style={{ animationDelay: `${selectedItem.animation_delay || 0}s` }}
                            />
                          </div>
                        </div>
                      )}

                      <p className="text-[10px] text-purple-500/70 italic">
                        Las animaciones son visibles en la vista pública
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Opacidad</Label>
                      <Slider
                        value={[(selectedItem.opacity || 1) * 100]}
                        onValueChange={([value]) => handleUpdateItem("opacity", value / 100)}
                        min={10}
                        max={100}
                        step={5}
                        className="py-2"
                      />
                    </div>
                  </>
                )}

                {/* Text Properties */}
                {(selectedItem.type === 'name' || selectedItem.type === 'price') && (
                  <>
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        {selectedItem.type === "name" ? <Type className="w-4 h-4" /> : <DollarSign className="w-4 h-4" />}
                        Texto
                      </Label>
                      <Input
                        value={selectedItem.text || ""}
                        onChange={(e) => handleUpdateItem("text", e.target.value)}
                        className="bg-stone-50"
                        data-testid="edit-item-text-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Etiqueta (para identificar)</Label>
                      <Input
                        value={selectedItem.label || ""}
                        onChange={(e) => handleUpdateItem("label", e.target.value)}
                        placeholder="Ej: Pizza Margherita, Bebida 1..."
                        className="bg-stone-50"
                        data-testid="edit-item-label-input"
                      />
                      <p className="text-xs text-stone-400">Este nombre aparece en la lista móvil</p>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-stone-50 rounded-md">
                      <div>
                        <Label className="cursor-pointer">Sombra de texto</Label>
                        <p className="text-xs text-stone-400">Mejora la legibilidad</p>
                      </div>
                      <Switch
                        checked={selectedItem.text_shadow || false}
                        onCheckedChange={(checked) => handleUpdateItem("text_shadow", checked)}
                        data-testid="text-shadow-toggle"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Fuente</Label>
                      <Select
                        value={selectedItem.font_family}
                        onValueChange={(value) => handleUpdateItem("font_family", value)}
                      >
                        <SelectTrigger className="bg-stone-50" data-testid="font-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FONTS.map(font => (
                            <SelectItem key={font.value} value={font.value}>
                              <span style={{ fontFamily: font.value }}>{font.label}</span>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Peso de Fuente</Label>
                      <Select
                        value={String(selectedItem.font_weight)}
                        onValueChange={(value) => handleUpdateItem("font_weight", parseInt(value))}
                      >
                        <SelectTrigger className="bg-stone-50" data-testid="font-weight-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {FONT_WEIGHTS.map(weight => (
                            <SelectItem key={weight.value} value={String(weight.value)}>
                              {weight.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Tamaño: {selectedItem.font_size}px</Label>
                      <Slider
                        value={[selectedItem.font_size]}
                        onValueChange={([value]) => handleUpdateItem("font_size", value)}
                        min={6}
                        max={72}
                        step={1}
                        className="py-2"
                        data-testid="font-size-slider"
                      />
                      <div className="flex gap-1 flex-wrap">
                        {[6, 8, 10, 12, 14, 16, 18, 20, 24, 28, 32, 40, 48].map(size => (
                          <Button
                            key={size}
                            variant={selectedItem.font_size === size ? "default" : "outline"}
                            size="sm"
                            className="h-6 px-2 text-xs"
                            onClick={() => handleUpdateItem("font_size", size)}
                          >
                            {size}
                          </Button>
                        ))}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Color</Label>
                      <div className="flex gap-2">
                        <input
                          type="color"
                          value={selectedItem.color}
                          onChange={(e) => handleUpdateItem("color", e.target.value)}
                          className="w-12 h-10 rounded border border-stone-200 cursor-pointer"
                          data-testid="color-picker"
                        />
                        <Input
                          value={selectedItem.color}
                          onChange={(e) => handleUpdateItem("color", e.target.value)}
                          className="bg-stone-50 flex-1"
                          data-testid="color-input"
                        />
                      </div>
                    </div>

                    {/* Animation Controls for Text */}
                    <div className="space-y-3 p-3 bg-gradient-to-br from-purple-50 to-pink-50 rounded-lg border border-purple-100">
                      <Label className="text-xs text-purple-700 uppercase tracking-wide flex items-center gap-1">
                        <Sparkles className="w-3 h-3" /> Animaciones
                      </Label>
                      
                      <div className="space-y-2">
                        <Label className="text-xs text-stone-600">Animación continua</Label>
                        <Select
                          value={selectedItem.animation || "none"}
                          onValueChange={(value) => handleUpdateItem("animation", value)}
                        >
                          <SelectTrigger className="bg-white" data-testid="text-animation-select">
                            <SelectValue placeholder="Seleccionar animación" />
                          </SelectTrigger>
                          <SelectContent>
                            {ANIMATIONS.map(anim => (
                              <SelectItem key={anim.value} value={anim.value}>
                                <span className="flex items-center gap-2">
                                  <span>{anim.icon}</span>
                                  <span>{anim.label}</span>
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-xs text-stone-600">Animación de entrada</Label>
                        <Select
                          value={selectedItem.entry_animation || "none"}
                          onValueChange={(value) => handleUpdateItem("entry_animation", value)}
                        >
                          <SelectTrigger className="bg-white" data-testid="text-entry-animation-select">
                            <SelectValue placeholder="Seleccionar entrada" />
                          </SelectTrigger>
                          <SelectContent>
                            {ENTRY_ANIMATIONS.map(anim => (
                              <SelectItem key={anim.value} value={anim.value}>
                                <span className="flex items-center gap-2">
                                  <span>{anim.icon}</span>
                                  <span>{anim.label}</span>
                                </span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {(selectedItem.animation && selectedItem.animation !== 'none') && (
                        <div className="space-y-2">
                          <Label className="text-xs text-stone-600">Retraso de animación</Label>
                          <Select
                            value={String(selectedItem.animation_delay || 0)}
                            onValueChange={(value) => handleUpdateItem("animation_delay", parseFloat(value))}
                          >
                            <SelectTrigger className="bg-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {[0, 0.2, 0.4, 0.6, 0.8, 1, 1.5, 2, 3].map(delay => (
                                <SelectItem key={delay} value={String(delay)}>
                                  {delay === 0 ? 'Sin retraso' : `${delay}s`}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}

                      {/* Animation Preview */}
                      {(selectedItem.animation && selectedItem.animation !== 'none') && (
                        <div className="space-y-2">
                          <Label className="text-xs text-stone-600">Vista previa</Label>
                          <div className="bg-stone-800 rounded-lg p-4 flex items-center justify-center h-20 overflow-hidden">
                            <div 
                              className={`w-10 h-10 bg-gradient-to-br from-purple-400 to-pink-400 rounded-lg anim-${selectedItem.animation}`}
                              style={{ animationDelay: `${selectedItem.animation_delay || 0}s` }}
                            />
                          </div>
                        </div>
                      )}

                      <p className="text-[10px] text-purple-500/70 italic">
                        Las animaciones son visibles en la vista pública
                      </p>
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="p-4">
              <Card className="border-dashed border-stone-300 bg-stone-50/50">
                <CardContent className="py-8 text-center">
                  <Type className="w-8 h-8 mx-auto text-stone-400 mb-2" />
                  <p className="text-stone-500 text-sm">
                    {t('selectItemOnCanvas')}
                  </p>
                </CardContent>
              </Card>

              {/* Items list */}
              <div className="mt-6">
                <h3 className="font-semibold text-stone-900 mb-3">{t('menuItems')}</h3>
                {menu?.items?.length > 0 ? (
                  <div className="space-y-2">
                    {menu.items.map((item) => {
                      // Get icon based on item type
                      const getItemIcon = () => {
                        switch(item.type) {
                          case 'image': return '🖼️';
                          case 'video': return '🎬';
                          case 'gif': return '🎞️';
                          case 'shape': return '⬜';
                          case 'clock': return '🕐';
                          case 'weather': return '🌤️';
                          case 'ticker': return '📢';
                          case 'rss': return '📰';
                          case 'name': return <Type className="w-3 h-3 text-stone-400" />;
                          case 'price': return <DollarSign className="w-3 h-3 text-stone-400" />;
                          default: return <Type className="w-3 h-3 text-stone-400" />;
                        }
                      };

                      // Get description based on item type
                      const getItemDescription = () => {
                        switch(item.type) {
                          case 'image': return `${getToastMsg(language, 'image')} • ${item.width?.toFixed(0) || 0}% x ${item.height?.toFixed(0) || 0}%`;
                          case 'video': return `${getToastMsg(language, 'video')} • ${item.video_type || 'mp4'}`;
                          case 'gif': return `GIF`;
                          case 'shape': return `${t('shape')} ${item.shape_type || 'rectangle'} • ${item.width?.toFixed(0) || 0}% x ${item.height?.toFixed(0) || 0}%`;
                          case 'clock': return `${t('clock')} • ${item.clock_format || '12h'}`;
                          case 'weather': return `${t('weather')} • ${item.weather_city || t('city')}`;
                          case 'ticker': return `Ticker • ${item.ticker_speed || 30}s`;
                          case 'rss': return `RSS • ${item.rss_preset || 'Custom'}`;
                          default:
                            return `${item.type === "name" ? getToastMsg(language, 'name') : getToastMsg(language, 'price')} • ${item.font_size || 16}px${item.text_shadow ? ` • ${getToastMsg(language, 'shadow')}` : ""}`;
                        }
                      };

                      return (
                        <div
                          key={item.id}
                          className="flex items-center justify-between p-3 bg-stone-50 rounded-md hover:bg-stone-100 cursor-pointer transition-colors"
                          onClick={() => setSelectedItems([item])}
                          data-testid={`item-list-${item.id}`}
                        >
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="text-sm">{getItemIcon()}</span>
                              <p className="font-medium text-stone-800 text-sm">{item.label || item.text || 'Item'}</p>
                            </div>
                            <p className="text-stone-400 text-xs mt-1">
                              {getItemDescription()}
                            </p>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteItem(item.id);
                            }}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-stone-400 text-sm text-center py-4">
                    No hay items aún
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Media Library Dialog */}
      <MediaLibrary
        open={isLibraryOpen}
        onOpenChange={setIsLibraryOpen}
        token={token}
        mode={libraryMode}
        onSelect={handleUseMedia}
        language={language}
      />
    </div>
  );
};

export default EditorPage;
